/* Audio Converter tool core — /convert.
 *
 * Architecture (2026-07-09 root-cause rebuild):
 *   The tool used to carry its OWN FFmpeg loader/instance/retry stack inline
 *   in the blade (~250 lines duplicating ffmpeg-processor.js minus all of its
 *   medicine: multi-source retry ladder, SW-truncation self-heal, SIMD/WASM
 *   preflight, FILE_LOST guard via FileBytes, error classification with
 *   localized user messages, reinit-on-corruption). Every conversion now goes
 *   through the ONE shared engine:
 *
 *     1. gate      — AppleDouble junk, empty files, extension sniff by magic
 *                    bytes (files with no/wrong extension get a real format).
 *     2. lite      — MediaPipeline (WebAudio decode + lamejs/WebCodecs/WAV
 *                    encode) for mp3/wav/flac targets when the source is
 *                    browser-decodable. No 31 MB WASM for the common case.
 *     3. ffmpeg    — FFmpegProcessor.process() with args built from the
 *                    server-generated codec map. The old inline path lost
 *                    `-f <container>` for every noBitrate format whenever
 *                    QualityModule was live (QM args replaced the WHOLE codec
 *                    array) → cdda/raw/pcm/alac/snd/f32le/s16le failed 100%
 *                    with "Unable to find a suitable output format". Args are
 *                    now: [container] + (bit-depth codec | codec map) so the
 *                    muxer flag can never be dropped.
 *     4. bridge    — if FFmpeg cannot DECODE the input (time_base 1/0 class:
 *                    VBR mp3 with broken frame headers that browsers happily
 *                    play) but WebAudio can, decode in the browser, hand
 *                    FFmpeg a clean WAV, encode to the real target.
 *     5. server    — legacy Go microservice for encoder-less formats
 *                    (amr/dts/ra), single file, unchanged.
 *
 * The blade keeps markup/CSS only; config arrives via window.__CONVERT_CFG.
 * This file ships through @light → js-min (es2019) so older Safari parses it.
 */
(function () {
    'use strict';

    var CFG = window.__CONVERT_CFG;
    if (!CFG) return; // not the convert page
    var t = CFG.t || {};

    // ========== Format maps (server-generated, single source of truth) ==========
    var FORMAT_DATA = CFG.formatData || {};
    var BROWSER_FORMATS = CFG.browserFormats || [];
    var INPUT_FORMATS = CFG.inputFormats || [];
    var FFMPEG_CODECS = CFG.codecs || {};
    var MIME_TYPES = CFG.mimeTypes || {};
    var EXT_MAP = CFG.extMap || {};
    var LOSSLESS_ALL = CFG.lossless || [];
    var FORMATS = {};
    Object.keys(FORMAT_DATA).forEach(function (key) {
        var val = FORMAT_DATA[key];
        FORMATS[key] = Object.assign({}, val, { mode: BROWSER_FORMATS.indexOf(key) !== -1 ? 'browser' : 'server' });
    });

    // Formats decodeAudioData is known to handle in at least one major browser.
    // A lite attempt on anything else (wma/amr/ape/...) is a guaranteed decode
    // failure — telemetry showed 25+/week wasted attempts before falling
    // through to FFmpeg anyway. Skip straight to FFmpeg for those.
    var WEBAUDIO_DECODABLE = ['mp3', 'wav', 'wave', 'flac', 'm4a', 'aac', 'mp4', 'mov', 'm4b', 'ogg', 'oga', 'opus', 'webm', 'aiff', 'aif', 'caf', '3gp'];
    // Formats whose encoder caps out at stereo — auto-downmix >2ch sources
    // instead of letting the encoder abort (5.1 WAV → mp3 was a hard fail).
    var MAX_STEREO = ['mp3', 'amr', 'gsm', 'g722', 'ra'];

    /* Потолок размера — ОДИН на весь сайт, а не константа этого тула.
     *
     * До 2026-08-09 здесь стояло `maxSize: 100 * 1024 * 1024` — число, выбранное
     * 2026-02-03, ещё до появления и адаптивного примитива, и потокового
     * WORKERFS-лейна. С тех пор все десять соседей по аудио-группе (trim,
     * volume, equalizer, denoise, join, channel-converter, …) перешли на
     * `max-mb="auto"`, а /convert остался единственным с рукописным числом.
     *
     * Чем это было на самом деле — замер 30 дней: 108 выборов файла крупнее
     * 100 МБ, и у 96 из них общий примитив УЖЕ говорил «можно» (`overHint:0`
     * при `hintMB` 869–1024). То есть тул отказывал файлу, который сам сайт
     * этому устройству разрешил. Жалоба #1337 (evgzubarev@mail.ru, Премиум):
     * 613 МБ FLAC 24/192, три попытки на двух тулах, `filebytes_spill_ok`,
     * движок инициализировался — и ни одного результата.
     *
     * Что реально держит нагрузку — тоже замер, а не ощущение: 448 МБ FLAC
     * 24/192 → MP3 192k проходит за 118 с через `path:"stream"` при пике кучи
     * 497 МБ; на соседях с адаптивным капом файлы 300–600 МБ доезжают в 68.1%
     * сессий против 76.3% у файлов до 100 МБ (30 дней, 4183 выбора >100 МБ).
     * Разницы, которая оправдывала бы стену, нет.
     *
     * Поэтому число берётся ЛЕНИВО из общего примитива на каждый вызов: так
     * запись автоматически наследует и кламп бескомпромиссных устройств
     * (no-SIMD → 150 МБ), и iOS-поправку, и потолок 1 ГБ для kind `av`.
     * Серверный лейн (Go-микросервис, форматы без браузерного энкодера) —
     * другая история: там платим мы, и 30 МБ остаётся жёстким числом.
     */
    function browserMaxSize() {
        return (window.__effectiveFileLimit && window.__effectiveFileLimit('av'))
            || (window.__FILE_LIMITS && window.__FILE_LIMITS.av)
            || 100 * 1024 * 1024; // примитив ещё не поднялся — прежнее число как пол
    }
    var LIMITS = {
        browser: { maxFiles: 500, maxSize: 0 /* см. browserMaxSize() */, maxTotal: 2 * 1024 * 1024 * 1024 },
        server: { maxFiles: 1, maxSize: 30 * 1024 * 1024, maxTotal: 30 * 1024 * 1024 },
    };
    var HISTORY_KEY = 'timbrica_history_v2';
    var SETTINGS_KEY = 'timbrica_settings';

    var formatMessage = function (template, vars) {
        return String(template || '').replace(/:([a-zA-Z_]+)/g, function (m, key) {
            return Object.prototype.hasOwnProperty.call(vars || {}, key) ? vars[key] : m;
        });
    };
    var ae = function (type, payload) { try { if (window.__ae) window.__ae(type, payload); } catch (e) {} };
    var aeF = function (f, extra) { ae('feature_use', Object.assign({ slug: 'convert', f: f }, extra || {})); };
    var toast = function (msg, type) { try { if (window.__toast) { window.__toast(msg, type); return; } } catch (e) {} try { console.warn('[convert]', msg); } catch (e2) {} };

    // ========== State ==========
    var files = [];
    var idCounter = 0;
    var isConverting = false;
    var convertCancelled = false;
    var globalFormat = 'mp3';
    var currentlyPlaying = null;
    var formatPreset = !!CFG.formatPreset;
    var pickerTargetId = null; // null → global format picker; id → per-file picker

    // ========== DOM ==========
    var $ = function (id) { return document.getElementById(id); };
    var uploadZone = $('uploadZone');
    if (!uploadZone) return;
    var fileSection = $('fileSection');
    var fileList = $('fileList');
    var fileCountBadge = $('fileCountBadge');
    var actionBar = $('actionBar');
    var selectedCountEl = $('selectedCount');
    var convertBtn = $('convertBtn');
    var convertBtnText = $('convertBtnText');
    var downloadAllBtn = $('downloadAllBtn');
    var downloadZipBtn = $('downloadZipBtn');
    var progressOverlay = $('progressOverlay');
    var progressBar = $('progressBar');
    var progressFile = $('progressFile');
    var progressCount = $('progressCount');
    var progressTitle = $('progressTitle');
    var convertCancelBtn = $('convertCancelBtn');
    var previewAudio = $('previewAudio');
    var formatSelectorBtn = $('formatSelectorBtn');
    var formatSelectorText = $('formatSelectorText');
    var formatSelectorDropdown = $('formatSelectorDropdown');

    // ========== Utilities ==========
    function formatSize(b) {
        if (b < 1024) return b + ' B';
        if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
        // Гигабайты появились, когда потолок размера стал адаптивным: на обычной
        // машине он равен 1 ГБ, и сообщение «Файл больше 1024.0 MB» читается как
        // сбой вёрстки, а не как цифра. Целое число ГБ печатаем без дробной части.
        if (b >= 1073741824) return (b / 1073741824).toFixed(b % 1073741824 === 0 ? 0 : 1) + ' GB';
        return (b / 1048576).toFixed(1) + ' MB';
    }
    function getExt(name) {
        var i = name.lastIndexOf('.');
        return i > 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : '';
    }
    function baseName(name) { return name.replace(/\.[^.]+$/, ''); }
    function formatDuration(sec) {
        var m = Math.floor(sec / 60), s = Math.floor(sec % 60);
        return m + ':' + (s < 10 ? '0' : '') + s;
    }
    /* Подпись частоты: 44100 → «44.1k», 192000 → «192k». Раньше подпись
     * собиралась в трёх местах по-разному — в разметке блейда стояло «44k», а
     * ветка перестроения списка давала «44.1k» на том же контроле. */
    function rateLabel(r) {
        var n = parseInt(r, 10);
        if (!isFinite(n) || n < 1000) return String(r);
        var k = n / 1000;
        return (Math.round(k) === k ? k : k.toFixed(k < 100 ? 2 : 1).replace(/0$/, '')) + 'k';
    }

    function getFormatType(ext) {
        var fmt = FORMATS[ext];
        if (fmt) return fmt.type;
        if (INPUT_FORMATS.indexOf(ext) !== -1) return 'lossy';
        return 'unknown';
    }
    function outExtFor(fmt) { return EXT_MAP[fmt] || fmt; }
    function getLimits() {
        var mode = (FORMATS[globalFormat] || {}).mode || 'server';
        var lim = LIMITS[mode];
        if (mode !== 'browser') {
            // Потолок серверного лейна правится в админке (/pino/server-lane) и
            // приезжает в __tierLimits.server_lane_mb; 30 МБ остаётся полом до
            // бутстрапа и на случай его провала.
            var srvMb = (window.__serverLaneMb ? window.__serverLaneMb('convert', 30) : 30) * 1024 * 1024;
            return { maxFiles: lim.maxFiles, maxSize: srvMb, maxTotal: srvMb };
        }
        // Читаем потолок на каждый вызов: адаптивный примитив поднимается
        // асинхронно вместе с layout-скриптом, а выбор файла может случиться
        // раньше — замороженное на старте число снова стало бы константой.
        return { maxFiles: lim.maxFiles, maxSize: browserMaxSize(), maxTotal: lim.maxTotal };
    }

    // ========== Magic-byte sniffing ==========
    // Users drop files with no extension ("Столица закатов") or macOS
    // AppleDouble resource forks ("._Дорожка 01.aiff", magic 00 05 16 07).
    // Read the first bytes once and either assign a real format or reject
    // with a specific reason instead of a generic "unsupported".
    function sniffHeader(file) {
        return new Promise(function (resolve) {
            var reader = new FileReader();
            reader.onload = function () {
                try {
                    var b = new Uint8Array(reader.result);
                    resolve(sniffBytes(b));
                } catch (e) { resolve(null); }
            };
            reader.onerror = function () { resolve(null); };
            try { reader.readAsArrayBuffer(file.slice(0, 16)); } catch (e) { resolve(null); }
        });
    }
    function sniffBytes(b) {
        if (!b || b.length < 4) return null;
        if (b[0] === 0x00 && b[1] === 0x05 && b[2] === 0x16 && b[3] === 0x07) return 'appledouble';
        if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return 'mp3'; // ID3
        if (b[0] === 0xFF && (b[1] & 0xE0) === 0xE0) return 'mp3'; // MPEG sync (mp3/aac ADTS — mp3 demux hint is fine for both)
        if (b[0] === 0x66 && b[1] === 0x4C && b[2] === 0x61 && b[3] === 0x43) return 'flac'; // fLaC
        if (b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) return 'ogg'; // OggS
        if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) return 'wav'; // RIFF
        if (b[0] === 0x46 && b[1] === 0x4F && b[2] === 0x52 && b[3] === 0x4D) return 'aiff'; // FORM
        if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) return 'webm'; // EBML
        if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'm4a'; // ftyp
        if (b[0] === 0x4D && b[1] === 0x41 && b[2] === 0x43 && b[3] === 0x20) return 'ape'; // MAC<sp>
        if (b[0] === 0x77 && b[1] === 0x76 && b[2] === 0x70 && b[3] === 0x6B) return 'wv'; // wvpk
        return null;
    }

    // ========== Settings ==========
    function loadSettings() {
        var s = {};
        try { s = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch (e) {}
        if (s.format && FORMATS[s.format]) globalFormat = s.format;
        updateQualitySelects();
        try {
            /* ⚠️ КАНАЛОВ ЗДЕСЬ НЕТ НАМЕРЕННО.
             *
             * Раскладка — свойство КОНКРЕТНОГО файла, а не вкус пользователя, и
             * общий ChannelModule по этой причине держит выбор только в пределах
             * сессии: в его комментарии записан инцидент, где сохранённое
             * «Стерео» три недели спустя молча подмешало каналы моно-записи на
             * /acx-mastering. /convert же сохранял `channels` в localStorage и
             * восстанавливал по одному лишь наличию <option> — не глядя на
             * disabled. То есть выбранное однажды «5.1» возвращалось на моно-
             * файл, где апмикс невозможен (нет бокового сигнала), и уезжало как
             * `-ac 6` = пять пустых каналов. Ровно та подделка, которую эта
             * правка и убирает, только с чёрного хода и через недели.
             *
             * Формат, битрейт и частота остаются: это действительно предпочтения,
             * и они не зависят от того, что за файл принесли.
             *
             * «128» без пометки `v` — запись до «Авто» (2026-10-08): её писала
             * любая смена формата, и почти у всех вернувшихся там 128, которого
             * никто не выбирал. «Авто» кодирует те же 128 и вдобавок копирует
             * звук видео, уже лежащий в формате цели. Выбранное после этой
             * даты 128 пишется с `v: 2` и остаётся 128. */
            if (s.bitrate === '128' && !s.v) s.bitrate = 'auto';
            ['bitrate', 'sampleRate'].forEach(function (k) {
                if (!s[k]) return;
                var el = $(k === 'bitrate' ? 'bitrateSelect' : 'sampleRateSelect');
                var opt = el && el.querySelector('option[value="' + s[k] + '"]');
                if (opt && !opt.disabled) el.value = s[k];
            });
        } catch (e) {}
    }
    function saveSettings() {
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify({
                v: 2,
                format: globalFormat,
                bitrate: $('bitrateSelect').value,
                sampleRate: $('sampleRateSelect').value,
            }));
        } catch (e) {}
    }

    // ========== Settings of ONE file ==========
    /* Селекты качества одни на всю очередь и говорят на языке ОБЩЕГО формата:
     * «Битрейт» (kbps) у lossy, «Разрядность» у wav/flac/aiff. Чип строки даёт
     * файлу СВОЙ формат — и до 2026-10-08 такой файл получал чужое число:
     *   общий FLAC «Исходное» + у файла M4A → `-b:a sourcek`: FFmpeg падал,
     *     мост падал теми же аргументами — «Ошибка обработки» на годном WAV;
     *   общий WAV «16 бит» + у файла MP3 → `-b:a 16k` или lite-лейн на
     *     16 кбит/с — молча, файл «готов»;
     *   общий MP3 «320» + у файла FLAC → 320 читалось как глубина;
     *   общий FLAC 96 кГц + у файла MP3 → `-ar 96000` сырым значением селекта.
     * Замерено на реальной паре MP3→FLAC + WAV→M4A (тест
     * convert-format-menu-reachable). Число теперь решается по РОДУ формата
     * ФАЙЛА: род общего формата — с селекта; другой род — последнее,
     * выбранное в том роде на этой странице, иначе его умолчание (глубина —
     * «Исходное», битрейт — середина лестницы формата), под потолком тарифа.
     * Частоту и каналы зажимает план (outputPlan) под формат файла. */
    var qualityMemory = { kbps: null, bits: 'source' };
    function qualityKind(fmt) {
        return (FORMAT_DATA[fmt] || {}).noBitrate ? 'bits' : 'kbps';
    }
    /* Селект помнит свой род в data-quality-gate (его ставят и этот тул, и
       QualityModule) — значение уходит в память ЭТОГО рода до перестройки. */
    function rememberQuality() {
        var sel = $('bitrateSelect');
        if (!sel || !sel.value) return;
        qualityMemory[sel.getAttribute('data-quality-gate') === 'bits' ? 'bits' : 'kbps'] = sel.value;
    }
    function kbpsFor(fmt, wish) {
        var list = ((FORMAT_DATA[fmt] || {}).bitrates || ['64', '128', '192', '256', '320']).map(String);
        var want = parseInt(wish, 10);
        // No number asked for («Авто», nothing remembered): what a fresh visitor
        // always got from the markup — 128 where the format has it.
        if (!isFinite(want)) want = list.indexOf('128') !== -1 ? 128 : parseInt(list[Math.floor(list.length / 2)], 10);
        try { if (window.__qualityTier) want = window.__qualityTier.clamp('kbps', want); } catch (_) {}
        // Наибольшая ступень лестницы формата не выше желаемого; нет такой —
        // наименьшая ступень (порядок лестницы в реестре не важен).
        var pick = null, low = null;
        list.forEach(function (b) {
            var n = parseInt(b, 10);
            if (!isFinite(n)) return;
            if (n <= want && (pick === null || n > parseInt(pick, 10))) pick = b;
            if (low === null || n < parseInt(low, 10)) low = b;
        });
        return pick !== null ? pick : (low !== null ? low : String(want));
    }
    function settingsFor(f) {
        var fmt = (f && f.target) || globalFormat;
        var st = {
            bitrate: ($('bitrateSelect') || {}).value,
            sampleRate: ($('sampleRateSelect') || {}).value,
            channels: ($('channelSelect') || {}).value
        };
        var kind = qualityKind(fmt);
        if (kind !== qualityKind(globalFormat)) {
            st.bitrate = kind === 'kbps' ? (qualityMemory.kbps || 'auto') : (qualityMemory.bits || 'source');
        }
        // «Авто» means «as it came»: a video whose sound already IS the target
        // codec gives that sound as it is. A number picked by hand is a request
        // to encode and always wins. `asItCame` is the request; `copy` — what the
        // row can promise from the plan so far. The run decides again once the
        // track is in hand (nextFile): a click that beats the header read still
        // gets its copy.
        st.asItCame = st.bitrate === 'auto';
        st.copy = st.asItCame && copyableTrack(f, fmt, st);
        if (kind === 'kbps' && st.bitrate === 'auto') st.bitrate = kbpsFor(fmt, null);   // what an encode gets instead
        return st;
    }
    /* Апмикс (5.1/7.1) выбран на общем селекторе каналов — файлу он положен,
       только если его формат унесёт столько каналов (план уже зажал их по
       реестру). Иначе MP3 из чипа получал матрицу на шесть каналов. */
    function upmixFor(plan) {
        var up = upmixTarget();
        return (up && plan && plan.channels >= up) ? up : null;
    }

    // ========== Video sources: the SOUND of the video, not the video ==========
    /* До 2026-10-08 видео шло в лейны целиком. Замер на настоящих роликах
     * (2 мин, H.264/HEVC + AAC): «M4A» из mp4 — 396 с, из mov — 225 с, из mkv —
     * 238 с, и в каждом «.m4a» лежала ПЕРЕКОДИРОВАННАЯ картинка H.264 ~700 кбит/с
     * (11–13 МБ вместо 2): у FFmpeg-аргументов не было ни -vn, ни -map, а ipod-
     * муксер берёт видео. lite-лейн при этом читал весь ролик в память.
     *
     * Теперь видео отдаёт звук общему примитиву MediaAudioTrack (правило дома:
     * «звук видео в аудиотуле — дорожкой», образец /trim):
     *   · план — при выборе файла, по заголовку: кодек, частота, каналы, есть
     *     ли звук вообще (нет — строка говорит это сразу, а не после «Конвертировать»);
     *   · сама дорожка — бухгалтерия moov для AAC/ALAC в mp4/mov/m4v/3gp
     *     (миллисекунды, ни байта сэмплов через JS) или FFmpeg -c:a copy;
     *   · формат цели — тот же кодек и битрейт «Авто» → файл отдаётся как
     *     есть: пакеты побайтно равны исходным (framemd5), сэмплы тоже (md5
     *     PCM); сама перепаковка — десятки миллисекунд (77 МБ запись — 58 мс,
     *     замер /trim 01.10); иначе лейны кодируют уже ЗВУК, не ролик.
     * Копия не идёт через потолок качества тарифа: кодировать нечего, это
     * собственный поток человека — как «M4A (исходный)» в /trim и «Оригинал» в
     * /extract-audio. Копируются только СЖАТЫЕ с потерями дорожки — там
     * перекодирование может только терять. ALAC/FLAC/PCM кодируются: их кодер
     * без потерь и так, а копия обошла бы потолок разрядности гостя («Исходное»
     * зажимается, Фарид 2026-07-19), и ALAC в .m4a не играют Chromium и Firefox. */
    // Target → the track codecs it takes as they are. Only targets whose file IS
    // the extracted track's container: webm/mka would need Opus/Vorbis rewrapped.
    var COPY_INTO = {
        m4a: ['aac'], m4r: ['aac'], aac: ['aac'], mp3: ['mp3'], mp2: ['mp2'],
        opus: ['opus'], ogg: ['vorbis'], oga: ['vorbis'],
        ac3: ['ac-3', 'ac3'], eac3: ['e-ac-3', 'eac3']
    };
    function isVideoSource(f) {
        return !!(f && f.file && window.MediaAudioTrack && window.MediaAudioTrack.isVideoContainer(f.file));
    }
    function copyableTrack(f, fmt, st) {
        var tr = f && f.track;
        if (!f || !f.video || !tr || !tr.codec || tr.lane === 'encode' || tr.lane === 'none') return false;
        var ok = COPY_INTO[fmt];
        if (!ok || ok.indexOf(tr.codec) === -1) return false;
        // Hz / channels set by hand ask for a change — an encode, like a number
        // in the bitrate list. Not compared with the header: an HE-AAC header
        // names its CORE rate (16 kHz for a 32 kHz track), so «same as the
        // source» cannot be read from it. A format's own fixed rate (Opus 48 k)
        // is not a choice.
        if (!(FORMAT_DATA[fmt] || {}).fixedSampleRate && st.sampleRate && st.sampleRate !== 'auto') return false;
        if (st.channels && st.channels !== 'auto') return false;
        if (upmixTarget()) return false;
        return true;
    }
    /* What a video header may be trusted with as `f.src` — the facts the plan
     * and the Hz gate read. AAC with SBR / PS signalled implicitly declares its
     * CORE: committed fixture heaac-v2-implicit-core16k.mp4 says 16 kHz in its
     * sample entry and decodes at 32 kHz, so «Авто» → MP3 came out at 16 kHz,
     * half the band (2026-10-08, caught before landing). An SBR core runs at
     * ≤ 24 kHz, so an AAC header above that is plain LC and true (44.1/48 kHz —
     * nearly every video); at ≤ 24 kHz neither the rate nor mono/stereo (PS
     * turns a mono core into stereo) can be read from it. 3+ channels are real
     * either way. 0 = unknown: the decoder decides, as for every video before. */
    function headerFacts(p) {
        var sure = p.codec !== 'aac' || (p.sampleRate || 0) > 24000;
        return {
            sampleRate: sure ? (p.sampleRate || 0) : 0,
            channels: (sure || (p.channels || 0) >= 3) ? (p.channels || 0) : 0
        };
    }
    /* At pick: the header only (codec, rate, channels, is there sound at all).
     * The track itself is taken at conversion — a remux the primitive refuses
     * (fragmented mp4 from MediaRecorder / OBS) falls back to FFmpeg, and the
     * engine must not start on a mere pick. */
    function planTrack(f) {
        f.video = true;
        window.MediaAudioTrack.plan(f.file).then(function (p) {
            f.track = p;
            if (p.hasAudio === false) {
                f.supported = false;
                f.errorReason = 'noAudio';     // like 'unsupported': never selected, own line
                f.status = 'error';
                aeF('video_no_audio', { ext: f.ext });
                recalculateSelection();
                render();
                return;
            }
            var hf = headerFacts(p);
            if (!f.src && (hf.sampleRate > 0 || hf.channels > 0)) {
                f.src = { sampleRate: hf.sampleRate, channels: hf.channels, bits: 0, durationSec: 0, format: p.codec || '' };
                publishSourceHint();
                publishSourceChannels();
            }
            render();
        }, function () {});
    }
    /* What the lanes work on. For a video: its track, when the track comes out
     * WITHOUT loss (remux / stream copy — copyOnly: the primitive never encodes
     * an intermediate for us); otherwise the video itself, and the FFmpeg lane
     * keeps the picture out (-vn) and maps the chosen track. */
    function soundOf(f) {
        if (!f.video || !window.MediaAudioTrack) return Promise.resolve({ file: f.file, ext: f.ext, video: false });
        if (f.soundJob) return f.soundJob;
        var asVideo = function () { return { file: f.file, ext: f.ext, video: true, track: (f.track && f.track.track) || 0 }; };
        var tr = f.track;
        if (tr && tr.lane !== 'remux' && tr.lane !== 'copy') return Promise.resolve(asVideo());
        // A stream copy is FFmpeg's work: load it through this tool's door, so the
        // overlay shows the engine download instead of a silent wait.
        var engine = (tr && tr.lane === 'copy') ? ensureFFmpeg() : Promise.resolve();
        f.soundJob = engine.then(function () {
            return window.MediaAudioTrack.extract(f.file, { slug: 'convert', copyOnly: true });
        }).then(function (x) {
            f.sound = x;
            // the extraction read the header too — a pick whose own plan has not
            // landed yet (a click right after the pick) learns the track, and the
            // source facts, from it
            if (!f.track) f.track = { codec: x.codec, lane: x.lane, track: x.track || 0, channels: x.channels || 0, sampleRate: x.sampleRate || 0, hasAudio: true };
            if (!f.src) {
                var hf = headerFacts(f.track);
                if (hf.sampleRate > 0 || hf.channels > 0) f.src = { sampleRate: hf.sampleRate, channels: hf.channels, bits: 0, durationSec: 0, format: f.track.codec || '' };
            }
            return { file: x.file, ext: x.ext, codec: x.codec, lane: x.lane, video: false };
        }, function (e) {
            if ((e && e.code === 'no-audio') || (window.__isUserCancel && window.__isUserCancel(e))) { f.soundJob = null; throw e; }
            return asVideo();
        });
        return f.soundJob;
    }
    function workOf(f) { return f.work || { file: f.file, ext: f.ext, video: !!f.video }; }
    /* The copy itself: the track file under the target's name. ADTS (.aac) is
     * the same bitstream in another wrapper — FFmpeg rewraps the small track. */
    function deliverCopy(f, fmt, src) {
        var name = baseName(f.name) + '.' + outExtFor(fmt);
        var finish = function (blob) {
            aeF('pipeline_route', { pipeline: 'copy', codec: src.codec || '', lane: src.lane || '', reason: f.ext + '_to_' + fmt });
            return { name: name, blob: blob, url: URL.createObjectURL(blob), size: blob.size, copied: true };
        };
        if (fmt === 'aac') {
            return ensureFFmpeg().then(function () {
                return window.FFmpegProcessor.process(src.file, ['-c:a', 'copy', '-f', 'adts'], 'output.aac', 'input.' + src.ext);
            }).then(function (r) {
                if (!r || !r.blob || !r.blob.size) throw new Error('Empty output for ' + f.name);
                try { if (r.url) URL.revokeObjectURL(r.url); } catch (_) {}
                return finish(new File([r.blob], name, { type: MIME_TYPES[fmt] || 'audio/aac' }));
            });
        }
        return Promise.resolve(finish(new File([src.file], name, { type: MIME_TYPES[fmt] || src.file.type || 'audio/mp4' })));
    }

    // ========== Quality selects ==========
    /* Слова про kbps принадлежат блейду (они переведены в 10 локалях там), слова
     * про глубину — QualityModule (11 локалей внутри модуля). Запоминаем первую
     * пару из разметки, чтобы возврат к lossy-формату восстановил именно её, а
     * не оставил на списке 64…320 подпись «Разрядность». */
    var _kbpsWording = null;
    function kbpsWording(group) {
        if (!_kbpsWording) {
            var lab = group && group.querySelector('label');
            _kbpsWording = {
                label: lab ? lab.textContent : '',
                tip: (group && group.getAttribute('title')) || ''
            };
        }
        return _kbpsWording;
    }

    function updateQualitySelects() {
        rememberQuality();
        var format = globalFormat.toLowerCase();
        var formatInfo = FORMAT_DATA[format] || {};
        var bitrateSelect = $('bitrateSelect');
        var sampleRateSelect = $('sampleRateSelect');
        var bitrateGroup = bitrateSelect.closest('.quality-group');
        var sampleRateGroup = sampleRateSelect.closest('.quality-group');
        var defaultBitrates = ['64', '128', '192', '256', '320'];
        // Лестница частот приходит из config/formats.php (default_sample_rates).
        // Копия этого списка жила здесь и разошлась с реестром — в ней не было
        // ни 176.4k, ни 192k.
        var defaultSampleRates = [{ value: 'auto', label: t.auto }].concat(
            (CFG.defaultSampleRates || ['22050', '44100', '48000', '96000']).map(function (r) {
                return { value: String(r), label: rateLabel(r) };
            })
        );

        if (formatInfo.noBitrate) {
            // Bit-depth choice is meaningful only where we have per-depth codec
            // args (wav/flac/aiff). For cdda/raw/alac/... the codec map already
            // pins the sample format — showing kbps there was the UI half of
            // the cdda bug.
            var qmOk = window.QualityModule && !window.QualityModule.__isStub
                && typeof QualityModule.populateOptions === 'function'
                && ['wav', 'flac', 'aiff', 'aif'].indexOf(format) !== -1;
            if (qmOk) {
                bitrateGroup.classList.remove('hidden');
                /* Метка/подсказка переключаются вместе со списком (их знает
                 * модуль, в 11 локалях), а умолчание для глубины — «Исходное».
                 *
                 * Умолчание: конвертер обязан по умолчанию ВЕРНУТЬ то, что ему
                 * принесли. Прежним умолчанием было '16', поэтому 24-битный
                 * мастер терял восемь бит у всякого, кто не тронул контрол, —
                 * ровно тот случай, который уже ловили на /repair-audio
                 * («Исходное» там включено через preferSource). */
                QualityModule.populateOptions(bitrateSelect, format, {
                    labelEl: bitrateGroup.querySelector('label'),
                    preferredDefault: 'source',
                    lossyTip: kbpsWording(bitrateGroup).tip
                });
            } else {
                bitrateGroup.classList.add('hidden');
            }
        } else {
            bitrateGroup.classList.remove('hidden');
            // Обратный переход wav → mp3: список снова kbps, значит и подпись
            // снова «Битрейт» с подсказкой про kbps.
            var w = kbpsWording(bitrateGroup);
            var lossyLabel = bitrateGroup.querySelector('label');
            if (lossyLabel && w.label) lossyLabel.textContent = w.label;
            if (w.tip) { bitrateGroup.title = w.tip; bitrateSelect.title = w.tip; }
            var bitrates = formatInfo.bitrates || defaultBitrates;
            var currentBitrate = bitrateSelect.value;
            // «Авто» первым: звук видео в том же кодеке уходит копией, остальное —
            // 128 (то, что свежий посетитель всегда получал из разметки).
            bitrateSelect.innerHTML = '<option value="auto" data-qt-copy="1"' + (currentBitrate === 'auto' ? ' selected' : '') + '>' + escapeHtml(t.auto || 'Auto') + '</option>'
                + bitrates.map(function (b) {
                    return '<option value="' + b + '"' + (b === currentBitrate ? ' selected' : '') + '>' + b + '</option>';
                }).join('');
            if (currentBitrate !== 'auto' && bitrates.indexOf(currentBitrate) === -1) bitrateSelect.value = 'auto';
            // This branch builds the option list itself (no QualityModule), so it
            // owns the tier gate too: stamp the kind back to kbps — a previous
            // wav/flac pass may have left 'bits' on the element, and re-gating a
            // 64-320 kbps list against the 16-BIT ceiling would lock everything —
            // then close whatever sits above the plan's ceiling.
            try {
                bitrateSelect.setAttribute('data-quality-gate', 'kbps');
                if (window.__qualityTier) window.__qualityTier.gate(bitrateSelect, 'kbps');
            } catch (_) {}
        }

        if (formatInfo.fixedSampleRate) {
            sampleRateGroup.classList.remove('hidden');
            var rate = formatInfo.fixedSampleRate;
            sampleRateSelect.innerHTML = '<option value="' + rate + '" selected>' + rateLabel(rate) + ' (' + t.fixed + ')</option>';
            sampleRateSelect.disabled = true;
        } else if (formatInfo.sampleRates) {
            // 'auto' must head every constrained list. The old code fell back
            // to rates[0] when the current value wasn't in the list — for mp3
            // that was 22050 Hz, so every fresh visitor's default conversion
            // silently downsampled to half CD rate (measured: flac→mp3 output
            // at 22050/128k on a clean profile). 'auto' omits -ar and lets the
            // encoder keep the source rate.
            sampleRateGroup.classList.remove('hidden');
            sampleRateSelect.disabled = false;
            var rates = ['auto'].concat(formatInfo.sampleRates);
            var currentRate = sampleRateSelect.value;
            sampleRateSelect.innerHTML = rates.map(function (r) {
                var lbl = r === 'auto' ? t.auto : rateLabel(r);
                return '<option value="' + r + '"' + (r === currentRate ? ' selected' : '') + '>' + lbl + '</option>';
            }).join('');
            if (rates.indexOf(currentRate) === -1) sampleRateSelect.value = 'auto';
        } else {
            sampleRateGroup.classList.remove('hidden');
            sampleRateSelect.disabled = false;
            var cur = sampleRateSelect.value;
            sampleRateSelect.innerHTML = defaultSampleRates.map(function (r) {
                return '<option value="' + r.value + '"' + (r.value === cur ? ' selected' : '') + '>' + r.label + '</option>';
            }).join('');
        }

        var channelSelect = $('channelSelect');
        var channelGroup = $('channelGroup');
        channelGroup.classList.remove('hidden');
        /* Список каналов принадлежит ОБЩЕМУ ChannelModule.
         *
         * ⚠️ Здесь стоял свой список из пяти вариантов и свой потолок формата —
         * локальный MAX_STEREO из семи имён. Это давало два дефекта сразу,
         * оба замерены на живой странице 2026-09-03:
         *
         *  1. ФАЛЬШИВЫЙ ОБЪЁМНЫЙ ЗВУК. «5.1» предлагалось для СТЕРЕО-источника
         *     и уезжало как `-ac 6`: на выходе 6 каналов, из них 4 — цифровая
         *     тишина (FC/LFE/BL/BR), у «7.1» пустых шесть из восьми. Файл с
         *     ярлыком 5.1, внутри которого стерео. Общий модуль такую опцию
         *     ЗАКРЫВАЕТ с подсказкой «исходник: Стерео», а если тул умеет
         *     настоящий апмикс (мы теперь умеем) — подписывает её честно.
         *  2. ПОТОЛОК ФОРМАТА ИЗ ПРОТУХШЕГО СПИСКА. В реестре
         *     (config/formats.php max_channels) потолок 2 канала стоит у
         *     шестнадцати форматов, а локальный список знал семь: wma, mp2,
         *     m4r, opus, webm, mmf, 8svx, cdda, voc, 3gp получали 5.1/7.1 в
         *     меню при кодеке, который держит два. Ровно тот же разрыв «реестр
         *     есть, а этот путь его не спрашивает», который уже чинили здесь
         *     для частоты — и который buildPlan уже НЕ повторяет: он читает
         *     __audioCodecLimits. UI просто остался на старом.
         *
         * Fail-open: если модуль не загрузился, остаётся прежний список — тул
         * не должен терять контрол из-за отсутствующего скрипта. */
        if (channelsLive()) {
            ChannelModule.updateForFormat(format);
        } else {
            var curCh = channelSelect.value;
            if (MAX_STEREO.indexOf(format) !== -1) {
                channelSelect.innerHTML = '<option value="auto">' + t.auto + '</option><option value="1">Mono</option><option value="2">Stereo</option>';
                channelSelect.value = (curCh === '6' || curCh === '8') ? 'auto' : (['auto', '1', '2'].indexOf(curCh) !== -1 ? curCh : 'auto');
            } else {
                channelSelect.innerHTML = '<option value="auto">' + t.auto + '</option><option value="1">Mono</option><option value="2">Stereo</option><option value="6">5.1</option><option value="8">7.1</option>';
                if (['auto', '1', '2', '6', '8'].indexOf(curCh) !== -1) channelSelect.value = curCh;
            }
        }
    }

    function channelsLive() {
        return !!(window.ChannelModule && !window.ChannelModule.__isStub
            && typeof ChannelModule.updateForFormat === 'function');
    }

    /* Целевая раскладка, которую можно только СИНТЕЗИРОВАТЬ из стерео.
     * null — обычный путь (`-ac N` достаточно). Число 6/8 — нужна матрица
     * UpmixModule; голый `-ac` здесь и есть та подделка, которую чиним. */
    function upmixTarget() {
        if (!channelsLive() || typeof ChannelModule.getUpmixTarget !== 'function') return null;
        try { return ChannelModule.getUpmixTarget() || null; } catch (_) { return null; }
    }

    /* Сколько каналов у САМОГО СЛАБОГО из выбранных файлов.
     *
     * Селектор каналов один на всю очередь, поэтому «источник» для него — это
     * минимум по батчу: если хоть один файл стерео, «5.1» для него придётся
     * либо синтезировать, либо закрыть. Максимум здесь соврал бы ровно на том
     * файле, которому нечем наполнить каналы. (Для частоты рядом публикуется
     * МАКСИМУМ — там смысл обратный: потолок тира не должен резать ничей
     * hi-res.) */
    function weakestSourceChannels() {
        var min = 0;
        files.forEach(function (f) {
            var c = (f.src && f.src.channels) || 0;
            if (!c) return;
            if (!min || c < min) min = c;
        });
        return min;
    }

    function publishSourceChannels() {
        if (!channelsLive()) return;
        var ch = weakestSourceChannels();
        if (ch > 0) { try { ChannelModule.detect(ch); } catch (_) {} }
    }

    // ========== Format picker (shared for global + per-file) ==========
    // ОДИН список на две двери: кнопка формата в панели действий (весь набор)
    // и чип «→ WAV» в строке файла (только этот файл). Показ, место у нажатой
    // кнопки и закрытие — js/anchored-popover.js (верхний слой); тул только
    // наполняет список. До 2026-10-08 список был position:absolute внутри
    // панели действий и уходил под сайдбар — разбор #1846 у CSS списка.
    var formatSearchQuery = '';
    var formatSearchInput = null, formatPickerFor = null, formatListEl = null;

    function syncFormatButton() {
        formatSelectorText.textContent = globalFormat.toUpperCase();
    }
    function pickerIsOpen() {
        return formatSelectorDropdown.getAttribute('data-pop-open') === 'true';
    }
    function pickerFile() {
        return pickerTargetId ? (files.find(function (x) { return x.id === pickerTargetId; }) || null) : null;
    }

    /* Каркас — поле поиска, строка «Формат для», список — строится ОДИН раз, а
       набор в поиске перерисовывает только список. Прежде на каждую букву
       пересоздавался и сам <input>: фокус возвращали руками, а набор через IME
       (хангыль собирается из нескольких нажатий) рвался на первом символе. */
    function buildFormatPicker() {
        if (formatListEl) return;
        formatSelectorDropdown.innerHTML = '<div class="format-search-wrap"><input type="text" class="format-search" id="formatSearchInput" autocomplete="off" spellcheck="false"></div>'
            + '<div class="format-picker-for" hidden></div>'
            + '<div class="format-list"></div>';
        formatSearchInput = $('formatSearchInput');
        formatPickerFor = formatSelectorDropdown.querySelector('.format-picker-for');
        formatListEl = formatSelectorDropdown.querySelector('.format-list');
        formatSearchInput.placeholder = t.search_placeholder || '';
        formatSearchInput.setAttribute('aria-label', t.search_placeholder || '');
        formatSearchInput.addEventListener('input', function () {
            formatSearchQuery = formatSearchInput.value;
            renderFormatList();
        });
        formatSearchInput.addEventListener('keydown', function (e) {
            var first = formatListEl.querySelector('.format-option:not(:disabled)');
            if (!first) return;
            // «fla» + Enter = FLAC: первый доступный вариант того, что набрано.
            if (e.key === 'Enter') { e.preventDefault(); chooseFormat(first.dataset.format); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); first.focus(); }
        });
        formatListEl.addEventListener('click', function (e) {
            var opt = e.target.closest && e.target.closest('.format-option');
            if (opt && !opt.disabled) chooseFormat(opt.dataset.format);
        });
        // Стрелки ходят по вариантам; вверх с первого — обратно в поиск.
        formatListEl.addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
            var opts = Array.prototype.slice.call(formatListEl.querySelectorAll('.format-option:not(:disabled)'));
            var i = opts.indexOf(document.activeElement);
            if (i === -1) return;
            e.preventDefault();
            if (e.key === 'Home') opts[0].focus();
            else if (e.key === 'End') opts[opts.length - 1].focus();
            else if (e.key === 'ArrowDown') (opts[i + 1] || opts[i]).focus();
            else if (i === 0) formatSearchInput.focus();
            else opts[i - 1].focus();
        });
    }

    function renderFormatList() {
        buildFormatPicker();
        var file = pickerFile();
        var selectedFmt = file ? (file.target || globalFormat) : globalFormat;
        var multipleFiles = files.length > 1;
        var query = formatSearchQuery.toLowerCase().trim();

        var filtered = Object.keys(FORMATS).filter(function (key) {
            var fmt = FORMATS[key];
            if (fmt.inputOnly) return false;
            if (!query) return true;
            return key.indexOf(query) !== -1 || fmt.name.toLowerCase().indexOf(query) !== -1 || fmt.desc.toLowerCase().indexOf(query) !== -1;
        });

        var groups = [
            { id: 'popular', name: t.categories.popular, formats: [] },
            { id: 'lossless', name: t.categories.lossless, formats: [] },
            { id: 'lossy', name: t.categories.lossy, formats: [] },
            { id: 'voice', name: t.categories.voice, formats: [] },
            { id: 'mobile', name: t.categories.mobile, formats: [] },
            { id: 'pro', name: t.categories.pro, formats: [] },
        ];
        var shown = {};
        filtered.forEach(function (key) {
            if (FORMATS[key].cat.indexOf('popular') !== -1 && !shown[key]) { groups[0].formats.push(key); shown[key] = 1; }
        });
        filtered.forEach(function (key) {
            if (shown[key]) return;
            var type = FORMATS[key].type;
            var g = type === 'lossless' ? groups[1] : type === 'voice' ? groups[3] : type === 'mobile' ? groups[4] : type === 'pro' ? groups[5] : groups[2];
            g.formats.push(key);
            shown[key] = 1;
        });

        if (file) {
            formatPickerFor.innerHTML = escapeHtml(t.format_for || 'Format for:') + ' <strong>' + escapeHtml(file.name) + '</strong>';
            formatPickerFor.hidden = false;
        } else {
            formatPickerFor.textContent = '';
            formatPickerFor.hidden = true;
        }

        var html = '';
        groups.forEach(function (group) {
            if (!group.formats.length) return;
            html += '<div class="format-cat">' + group.name + '</div>';
            group.formats.forEach(function (key) {
                var fmt = FORMATS[key];
                var isServer = fmt.mode === 'server';
                var isDisabled = isServer && multipleFiles;
                // Пометка — только та, что что-то говорит: «HQ» у без потерь и
                // «server». Пустая пилюля на каждом lossy-формате рисовалась
                // цветной чёрточкой и читалась как недорисованный значок.
                html += '<button type="button" class="format-option" data-format="' + key + '"'
                    + (key === selectedFmt ? ' aria-current="true"' : '')
                    + (isDisabled ? ' disabled title="' + escapeHtml(t.server_single || '') + '"' : '')
                    + '>'
                    + '<span class="fmt-name">' + fmt.name + '</span>'
                    + '<span class="fmt-desc">' + fmt.desc + '</span>'
                    + (fmt.type === 'lossless' ? '<span class="fmt-badge lossless">HQ</span>' : '')
                    + (isServer ? '<span class="fmt-badge server">server</span>' : '')
                    + '</button>';
            });
        });
        formatListEl.innerHTML = html || '<div class="format-no-results">' + t.no_results + '</div>';
    }

    function chooseFormat(chosen) {
        var file = pickerFile();
        if (file) {
            file.target = chosen;
            aeF('per_file_format', { fmt: chosen });
        } else {
            globalFormat = chosen;
            files.forEach(function (f) { f.target = chosen; });
            updateQualitySelects();
            recalculateSelection();
            saveSettings();
            aeF('global_format', { fmt: chosen });
        }
        closeFormatSelector();
        render();
    }

    /* Зовётся из клика по ЛЮБОЙ двери списка — до того, как браузер (или
       примитив) его покажет: список наполнен к первому кадру. */
    function prepareFormatSelector(fileId) {
        pickerTargetId = fileId || null;
        formatSearchQuery = '';
        buildFormatPicker();
        formatSearchInput.value = '';
        renderFormatList();
        // Фокус — в поиск, но не на тач-экране: там он поднял бы клавиатуру
        // поверх списка, который открыли, чтобы посмотреть.
        setTimeout(function () {
            if (pickerIsOpen() && !('ontouchstart' in window)) formatSearchInput.focus();
        }, 50);
    }
    function closeFormatSelector() {
        if (window.__anchoredPopover) { window.__anchoredPopover.close(formatSelectorDropdown); return; }
        try { if (formatSelectorDropdown.hidePopover) formatSelectorDropdown.hidePopover(); } catch (e) {}
    }
    /* render() пересобирает строки файлов, и открытый список терял бы чип, у
       которого стоит: Chromium и WebKit роняют такой список в угол (0,0). */
    function keepPickerAnchored() {
        if (!pickerIsOpen()) return;
        if (!pickerTargetId) { renderFormatList(); return; }
        var chip = pickerFile() && fileList.querySelector('.file-target[data-id="' + pickerTargetId + '"]');
        if (!chip) { closeFormatSelector(); return; }
        renderFormatList();
        if (window.__anchoredPopover && window.__anchoredPopover.anchorTo) window.__anchoredPopover.anchorTo(formatSelectorDropdown, chip);
    }
    function escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    // ========== File management ==========
    function recalculateSelection() {
        var lim = getLimits();
        var runningTotal = 0, selected = 0;
        files.forEach(function (f) {
            f.selected = false;
            if (f.errorReason === 'unsupported' || f.errorReason === 'appledouble' || f.errorReason === 'noAudio') return;
            if (f.size > lim.maxSize) {
                f.errorReason = 'tooLarge'; f.status = 'error';
                // Стена обязана быть ВИДНА в телеметрии. До 2026-08-09 отказ по
                // размеру не писал ничего: `last_error_type:null` в разборе #1337,
                // и единственным следом того, что человек упёрся, была его же
                // жалоба. Один раз на файл — recalculateSelection зовётся на
                // каждую перерисовку списка.
                if (!f.tooLargeReported) {
                    f.tooLargeReported = true;
                    aeF('size_wall', { file_size: f.size, cap: lim.maxSize, ext: f.ext || '' });
                }
                return;
            }
            if (selected >= lim.maxFiles) { f.errorReason = 'totalLimit'; f.status = 'error'; return; }
            if (runningTotal + f.size > lim.maxTotal) { f.errorReason = 'totalLimit'; f.status = 'error'; return; }
            f.selected = true;
            if (f.errorReason === 'tooLarge' || f.errorReason === 'totalLimit') f.errorReason = null;
            if (f.status === 'error' && !f.errorReason) f.status = 'ready';
            runningTotal += f.size;
            selected++;
        });
        return runningTotal;
    }

    function addFiles(newFiles) {
        var wasEmpty = files.length === 0;
        var firstExt = null;
        var lim = getLimits();
        var pending = [];

        Array.prototype.forEach.call(newFiles, function (file) {
            if (files.length + pending.length >= lim.maxFiles) return;

            var ext = getExt(file.name);
            // macOS Finder multi-select drags bring `._*` AppleDouble resource
            // forks along — 248-byte junk that looks like the real track.
            var isDotUnderscore = /^\._/.test(file.name);
            var fileObj = {
                id: ++idCounter,
                file: file,
                name: file.name,
                size: file.size,
                ext: ext,
                target: globalFormat,
                selected: false,
                supported: INPUT_FORMATS.indexOf(ext) !== -1,
                errorReason: null,
                status: 'ready',
                jobUrl: null, jobBlob: null, duration: null, audioUrl: null,
            };
            if (isDotUnderscore) {
                fileObj.supported = false;
                fileObj.errorReason = 'appledouble';
                fileObj.status = 'error';
            } else if (!fileObj.supported) {
                // Unknown/missing extension — sniff the real container before
                // rejecting (files named "Track 5" with no extension are mp3s
                // more often than not).
                fileObj.status = 'sniffing';
                pending.push(sniffHeader(file).then(function (kind) {
                    if (kind === 'appledouble') {
                        fileObj.errorReason = 'appledouble';
                        fileObj.status = 'error';
                    } else if (kind && INPUT_FORMATS.indexOf(kind) !== -1) {
                        fileObj.ext = kind;
                        fileObj.supported = true;
                        fileObj.status = 'ready';
                        fileObj.sniffed = true;
                        aeF('ext_sniffed', { to: kind, name_ext: ext || '(none)' });
                        attachPreview(fileObj);
                    } else {
                        fileObj.errorReason = 'unsupported';
                        fileObj.status = 'error';
                    }
                }));
            }
            if (fileObj.supported && file.size === 0) {
                fileObj.errorReason = 'empty';
                fileObj.status = 'error';
            }
            if (fileObj.supported && !fileObj.errorReason) {
                if (firstExt === null) firstExt = fileObj.ext;
                attachPreview(fileObj);
                probeHeader(fileObj);
                if (isVideoSource(fileObj)) planTrack(fileObj);
            }
            files.push(fileObj);
        });

        recalculateSelection();

        if (wasEmpty && firstExt && !formatPreset) {
            globalFormat = smartDefaultFormat(firstExt);
            files.forEach(function (f) { f.target = globalFormat; });
            updateQualitySelects();
        }
        if (files.length > 1 && (FORMATS[globalFormat] || {}).mode === 'server') {
            globalFormat = 'mp3';
            files.forEach(function (f) { f.target = globalFormat; });
            updateQualitySelects();
        }
        render();
        if (pending.length) Promise.all(pending).then(function () { recalculateSelection(); render(); });
    }

    function attachPreview(fileObj) {
        if (fileObj.audioUrl) return;
        try {
            var url = URL.createObjectURL(fileObj.file);
            fileObj.audioUrl = url;
            var audio = new Audio();
            audio.preload = 'metadata';
            audio.src = url;
            audio.onloadedmetadata = function () {
                fileObj.duration = audio.duration;
                var row = fileList.querySelector('.file-row[data-id="' + fileObj.id + '"] .file-meta');
                if (row && fileObj.duration && isFinite(fileObj.duration)) render();
            };
        } catch (e) {}
    }

    /* Read the source's own facts ONCE and KEEP them on the file object.
     *
     * ⚠️ Историческая причина, по которой это здесь главное:
     * раньше probeHeader звал AudioHeaderProbe, забирал из ответа ровно один бит
     * («заголовок разобрался или нет») и ВЫБРАСЫВАЛ частоту, глубину и каналы.
     * Дальше по файлу четыре независимых места пытались угадать то, что здесь
     * уже было прочитано, и каждое угадывало по-своему:
     *   · страж «формат тот же» сравнивал РАСШИРЕНИЕ → FLAC 192k → FLAC 96k
     *     объявлялся no-op и блокировался (жалоба пользователя 2026-08-11);
     *   · liteBlockedReason читал файл ВТОРОЙ раз и подменял вопрос «что уедет
     *     на выход» проверкой «а стоит ли Hz на auto» → стоило выбрать частоту
     *     явно, и hi-res уходил в lite-путь, где decodeAudioData режет всё до
     *     частоты контекста (замер: 192k → «96k», содержимого выше 24 кГц нет);
     *   · getSourceChannels читал поле _srcChannels, которое НИКТО никогда не
     *     заполнял → авто-даунмикс 5.1 → mp3 не срабатывал ни разу с момента
     *     написания;
     *   · тирный потолок Hz сравнивал абсолютное число с константой, не зная
     *     источника → 96k продавался как «выше», хотя для 192k это понижение.
     * Один прочитанный факт вместо четырёх догадок — поэтому f.src, а не ещё
     * одна локальная проверка. Всё, что ниже, спрашивает outputPlan(), а он —
     * только f.src.
     *
     * `null` остаётся законным значением: probe знает лишь wav/mp3/flac/aiff.
     * Неизвестный источник = «фактов нет», и план обязан деградировать в
     * прежнее поведение, а не выдумывать числа.
     */
    function probeHeader(fileObj) {
        if (['mp3', 'wav', 'wave', 'flac', 'aiff', 'aif'].indexOf(fileObj.ext) === -1) return;
        if (!window.AudioHeaderProbe || typeof window.AudioHeaderProbe.probe !== 'function') return;
        window.AudioHeaderProbe.probe(fileObj.file).then(function (p) {
            if (p && p.sampleRate > 0) {
                fileObj.src = {
                    sampleRate: p.sampleRate,
                    channels: p.channels || 0,
                    bits: p.bits || 0,
                    durationSec: p.durationSec || 0,
                    format: p.format || fileObj.ext
                };
                publishSourceHint();
                publishSourceChannels();
                render();
                return;
            }
            if (p === null && fileObj.status === 'ready') {
                // Don't hard-block: the decode bridge can often still rescue
                // these (browser decoders are more forgiving than the probe).
                fileObj.headerSuspect = true;
                aeF('upfront_corrupt_header_detected', { ext: fileObj.ext, file_size: fileObj.size, file_type: fileObj.file.type || '' });
                render();
            }
        }).catch(function () {});
    }

    /* Наибольшая исходная частота среди выбранных файлов — то, относительно чего
     * тирный потолок обязан считать «понижение». Публикуется на сам <select>,
     * потому что quality-tier.js по определению ничего не знает про файлы: он
     * общий для всех тулов и видит только контрол. */
    function publishSourceHint() {
        var sel = $('sampleRateSelect');
        if (!sel) return;
        var maxRate = 0;
        files.forEach(function (f) {
            if (f.selected && f.status !== 'success' && f.src && f.src.sampleRate > maxRate) maxRate = f.src.sampleRate;
        });
        var next = maxRate > 0 ? String(maxRate) : '';
        // Идемпотентность обязательна: gate() при перестановке значения шлёт
        // синтетический 'change', обработчик которого зовёт render(), а тот —
        // сюда же. Без сравнения это замкнулось бы в петлю.
        if ((sel.getAttribute('data-qt-source') || '') === next) return;
        if (next) sel.setAttribute('data-qt-source', next);
        else sel.removeAttribute('data-qt-source');
        try { if (window.__qualityTier) window.__qualityTier.gate(sel, 'hz'); } catch (_) {}
    }

    function smartDefaultFormat(sourceExt) {
        var ext = sourceExt.toLowerCase();
        if (ext === 'mid' || ext === 'midi') return 'mp3';
        if (LOSSLESS_ALL.indexOf(ext) !== -1 || ['ape', 'tak', 'shn'].indexOf(ext) !== -1) return 'mp3';
        if (ext === 'mp3') return 'wav';
        return 'mp3';
    }

    function removeFile(id) {
        var f = files.find(function (x) { return x.id === id; });
        if (f) {
            var row = fileList.querySelector('.file-row[data-id="' + id + '"]');
            var drop = function () {
                if (f.audioUrl && f.audioUrl.indexOf('blob:') === 0) { try { URL.revokeObjectURL(f.audioUrl); } catch (e) {} }
                files = files.filter(function (x) { return x.id !== id; });
                recalculateSelection();
                render();
            };
            if (row) {
                row.classList.add('removing');
                setTimeout(drop, 300);
                return;
            }
            drop();
            return;
        }
        recalculateSelection();
        render();
    }

    function clearAll() {
        files.forEach(function (f) {
            if (f.audioUrl && f.audioUrl.indexOf('blob:') === 0) { try { URL.revokeObjectURL(f.audioUrl); } catch (e) {} }
            if (f.jobUrl && f.jobUrl.indexOf('blob:') === 0 && f.jobUrl !== f.audioUrl) { try { URL.revokeObjectURL(f.jobUrl); } catch (e) {} }
        });
        files = [];
        render();
    }

    // ========== Audio preview ==========
    function togglePlay(id) {
        var f = files.find(function (x) { return x.id === id; });
        if (!f || !f.audioUrl) return;
        var prev = currentlyPlaying;
        if (currentlyPlaying === id) {
            previewAudio.pause();
            currentlyPlaying = null;
        } else {
            previewAudio.src = f.audioUrl;
            var p = previewAudio.play();
            currentlyPlaying = id;
            if (p && p.catch) {
                p.catch(function () {
                    // Browser can't play this container (flac/opus in some
                    // engines) — say so instead of a dead button (16 rage
                    // clicks on play-btn in 7d were exactly this silence).
                    currentlyPlaying = null;
                    updatePlayButtons(id, null);
                    toast(t.preview_unplayable || 'Preview not supported for this format in your browser — download to listen.', 'warning');
                });
            }
        }
        updatePlayButtons(prev, currentlyPlaying);
    }
    function updatePlayButtons(prevId, newId) {
        if (prevId) {
            var prevBtn = document.querySelector('.play-btn[data-id="' + prevId + '"]');
            if (prevBtn) {
                prevBtn.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>';
                var pr = prevBtn.closest('.file-row');
                if (pr) pr.classList.remove('playing');
            }
        }
        if (newId) {
            var newBtn = document.querySelector('.play-btn[data-id="' + newId + '"]');
            if (newBtn) {
                newBtn.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
                var nr = newBtn.closest('.file-row');
                if (nr) nr.classList.add('playing');
            }
        }
    }
    previewAudio.onended = function () {
        var prev = currentlyPlaying;
        currentlyPlaying = null;
        updatePlayButtons(prev, null);
    };
    previewAudio.onerror = function () {
        if (currentlyPlaying === null) return;
        var prev = currentlyPlaying;
        currentlyPlaying = null;
        updatePlayButtons(prev, null);
        toast(t.preview_unplayable || 'Preview not supported for this format in your browser — download to listen.', 'warning');
    };

    // ========== Render ==========
    /* Факты об источнике — «192k · 24 бита · 2 ch». Показываем ровно то, что
     * прочитано из заголовка: человеку, который пришёл менять частоту, надо
     * видеть, ОТ ЧЕГО он её меняет. Нет фактов — нет и строки. */
    function sourceFactsHtml(f) {
        if (!f.src || !f.src.sampleRate) return '';
        var parts = [rateLabel(f.src.sampleRate)];
        if (f.src.bits) parts.push(f.src.bits + ' ' + (t.bit_unit || 'bit'));
        if (f.src.channels) parts.push(f.src.channels + ' ch');
        return '<span class="file-src">' + escapeHtml(parts.join(' · ')) + '</span>';
    }

    /* Предсказанный размер результата. Показываем ТОЛЬКО когда он отличается от
     * исходного заметно (>2%) — иначе строка «→ 42.1 МБ» рядом с «42.0 МБ»
     * лишь шумит. `≈` ставится честно: точная формула есть у PCM и у кодека с
     * битрейтом, у сжатого lossless — оценка. */
    function predictedSizeHtml(f) {
        if (f.status === 'success' || !f.selected) return '';
        var st = settingsFor(f);
        if (st.copy) {
            var sz = (f.sound && f.sound.file && (f.sound.lane === 'remux' || f.sound.lane === 'copy')) ? f.sound.file.size : 0;
            return '<span class="file-predict file-copy" title="' + escapeHtml(t.copy_tip || '') + '">→ '
                + (sz ? formatSize(sz) + ' · ' : '') + escapeHtml(t.copy_tag || '') + '</span>';
        }
        var p = predictSize(f, outputPlan(f, f.target || globalFormat, st));
        if (!p || !p.bytes) return '';
        if (f.size && Math.abs(p.bytes - f.size) / f.size < 0.02) return '';
        return '<span class="file-predict" title="' + escapeHtml(t.predicted_size_tip || '') + '">→ '
            + (p.exact ? '' : '≈') + formatSize(p.bytes) + '</span>';
    }

    /* Строка под готовым файлом, чей звук «есть, но очень тихий» (soundLevel). */
    var FAINT = CFG.faint || null;
    function faintNoteHtml(f) {
        if (!FAINT || !f.sound || !f.sound.faint || !f.jobBlob) return '';
        return '<div class="converted-note" role="status" data-id="' + f.id + '">'
            + '<b>' + escapeHtml(FAINT.title || '') + '</b>'
            + escapeHtml(formatMessage(FAINT.desc || '', { db: String(f.sound.faint.needDb) }))
            + (FAINT.url ? '<br><a class="cv-door" href="' + escapeHtml(FAINT.url) + '" data-slug="volume" data-id="' + f.id + '">' + escapeHtml(FAINT.cta || '') + '</a>' : '')
            + '</div>';
    }

    function render() {
        publishSourceHint();   // состав выбранных файлов мог измениться
        // Серверный формат выбран → в блоке лимита появляется строка про
        // серверную квоту (1/день без Премиума); браузерный выбор её убирает.
        // render() дёргается на каждом изменении состояния (пикер, восстановление
        // настроек, авто-откат формата), поэтому одна точка покрывает все пути.
        if (window.__toolLimit && window.__toolLimit.serverLaneCase) {
            var slServer = files.length
                ? files.some(function (f) { return ((FORMATS[f.target || globalFormat] || {}).mode === 'server'); })
                : ((FORMATS[globalFormat] || {}).mode === 'server');
            window.__toolLimit.serverLaneCase('convert', slServer);
        }
        var hasFiles = files.length > 0;
        var pendingFiles = files.filter(function (f) { return f.status !== 'success'; });
        uploadZone.classList.toggle('hidden', hasFiles);
        fileSection.classList.toggle('show', pendingFiles.length > 0);
        actionBar.classList.toggle('show', hasFiles);
        fileCountBadge.textContent = files.length;
        syncFormatButton();

        var selectedFiles = files.filter(function (f) { return f.selected; });
        var readyFiles = selectedFiles.filter(function (f) { return f.status !== 'success'; });
        var successFiles = files.filter(function (f) { return f.status === 'success'; });
        var allDone = selectedFiles.length > 0 && readyFiles.length === 0 && successFiles.length > 0;
        selectedCountEl.textContent = readyFiles.length;

        var filesInfo = $('filesInfo');
        var newConvertBtn = $('newConvertBtn');
        var convertGroup = $('convertGroup');
        downloadAllBtn.classList.add('hidden');
        downloadZipBtn.classList.add('hidden');

        if (allDone) {
            filesInfo.innerHTML = '<span style="color: #22c55e;">✓ ' + t.done + '</span>';
            convertGroup.classList.add('hidden');
            newConvertBtn.classList.remove('hidden');
        } else if (successFiles.length > 0 && readyFiles.length > 0) {
            filesInfo.innerHTML = '<span style="color: #22c55e;">✓ ' + successFiles.length + '</span> / ' + selectedFiles.length;
            convertGroup.classList.remove('hidden');
            convertBtn.disabled = isConverting;
            newConvertBtn.classList.add('hidden');
        } else {
            filesInfo.innerHTML = t.to_convert + ' <strong>' + readyFiles.length + '</strong>';
            convertGroup.classList.remove('hidden');
            convertBtn.disabled = readyFiles.length === 0 || isConverting;
            newConvertBtn.classList.add('hidden');
        }

        var totalSelectedSize = selectedFiles.reduce(function (s, f) { return s + f.size; }, 0);

        fileList.innerHTML = pendingFiles.map(function (f) {
            var fmtType = getFormatType(f.ext);
            var isPlaying = currentlyPlaying === f.id;
            var errorMessage = '';
            if (f.errorReason === 'unsupported') errorMessage = t.unsupported;
            else if (f.errorReason === 'appledouble') errorMessage = t.appledouble || t.unsupported;
            else if (f.errorReason === 'empty') errorMessage = t.empty_file || t.unsupported;
            else if (f.errorReason === 'noAudio') errorMessage = t.no_audio_in_video || t.unsupported;
            // Подставляем ДЕЙСТВУЮЩИЙ потолок: он зависит от устройства, поэтому
            // число живёт в подстановке, а не в переводе (иначе на слабой машине
            // строка обещала бы больше, чем тул примет).
            else if (f.errorReason === 'tooLarge') errorMessage = formatMessage(t.too_large, { max: formatSize(getLimits().maxSize) });
            else if (f.errorReason === 'totalLimit') errorMessage = t.total_limit;
            else if (f.errorReason && typeof f.errorReason === 'string') errorMessage = f.errorReason;
            var warnMessage = (!errorMessage && f.headerSuspect) ? (t.header_suspect || '') : '';

            var rowClass = ['file-row', f.errorReason ? 'error' : '', f.status === 'processing' ? 'processing' : '', warnMessage ? 'warning' : ''].filter(Boolean).join(' ');
            var fileIndex = pendingFiles.indexOf(f) + 1;
            return '<div class="' + rowClass + '" data-id="' + f.id + '">'
                + '<span class="file-number">' + fileIndex + '</span>'
                + '<div class="file-status ' + (f.selected ? 'valid' : 'invalid') + '">'
                + (f.selected
                    ? '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>'
                    : '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>')
                + '</div>'
                + '<div class="file-info">'
                + '<div class="file-name-row">'
                + '<span class="file-name" title="' + escapeHtml(f.name) + '">' + escapeHtml(baseName(f.name)) + '</span>'
                + '<span class="file-ext ' + fmtType + '">' + escapeHtml(f.ext || '?') + (f.sniffed ? '*' : '') + '</span>'
                + (f.audioUrl ? '<button type="button" class="play-btn" data-id="' + f.id + '" aria-label="Play">'
                    + (isPlaying
                        ? '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>'
                        : '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>')
                    + '</button>' : '')
                + '</div>'
                + '<div class="file-meta">'
                + '<span class="file-size">' + formatSize(f.size) + '</span>'
                + (f.duration && isFinite(f.duration) ? '<span class="file-duration"><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' + formatDuration(f.duration) + '</span>' : '')
                + sourceFactsHtml(f)
                + predictedSizeHtml(f)
                + '</div>'
                + (errorMessage ? '<div class="file-error">' + escapeHtml(errorMessage) + '</div>' : '')
                + (warnMessage ? '<div class="file-warning">' + escapeHtml(warnMessage) + '</div>' : '')
                + '<div class="file-progress"><div class="file-progress-bar"></div></div>'
                + '</div>'
                + '<button type="button" class="file-target" data-id="' + f.id + '" popovertarget="formatSelectorDropdown" data-pop-align="end" title="' + (t.change_format_tip || '') + '">'
                + '<span class="file-arrow">→</span>'
                + '<span class="file-target-format">' + (f.target || globalFormat).toUpperCase() + '</span>'
                + '<svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>'
                + '</button>'
                + '<button type="button" class="file-remove" data-id="' + f.id + '" aria-label="Remove">'
                + '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
                + '</button>'
                + '</div>';
        }).join('');

        if (pendingFiles.length >= 2) {
            var summaryClass = totalSelectedSize > getLimits().maxTotal ? 'over-limit' : '';
            fileList.innerHTML += '<div class="files-summary ' + summaryClass + '">'
                + '<span class="summary-label">' + t.total_size + '</span>'
                + '<span class="summary-value">' + formatSize(totalSelectedSize) + '</span>'
                + (selectedFiles.length !== pendingFiles.length ? '<span class="summary-note">(' + selectedFiles.length + '/' + pendingFiles.length + ' ' + t.files_label + ')</span>' : '')
                + '</div>';
        }

        var convertedSection = $('convertedSection');
        var convertedList = $('convertedList');
        var convertedCount = $('convertedCount');
        var doneFiles = successFiles;
        if (doneFiles.length > 0) {
            convertedSection.classList.remove('hidden');
            convertedCount.textContent = doneFiles.length;
            $('convertedDownloadAllBtn').classList.toggle('hidden', doneFiles.length < 2);
            $('convertedDownloadZipBtn').classList.toggle('hidden', doneFiles.length < 2);
            convertedList.innerHTML = doneFiles.map(function (f) {
                return '<div class="converted-item" data-id="' + f.id + '">'
                    + '<svg aria-hidden="true" class="file-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
                    + (f.audioUrl ? '<button type="button" class="play-btn" data-id="' + f.id + '" aria-label="Play result">'
                        + (currentlyPlaying === f.id
                            ? '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>'
                            : '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>')
                        + '</button>' : '')
                    + '<span class="file-name" title="' + escapeHtml(f.name) + '">' + escapeHtml(baseName(f.name)) + '</span>'
                    + '<span class="file-format">' + (f.outputFormat || f.target || globalFormat).toUpperCase() + '</span>'
                    + '<span class="file-size">' + (f.outputSize ? formatSize(f.outputSize) : '') + '</span>'
                    + '<button type="button" class="file-download-btn" data-id="' + f.id + '">'
                    + '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
                    + t.download
                    + '</button>'
                    + '</div>'
                    + faintNoteHtml(f);
            }).join('');
        } else {
            convertedSection.classList.add('hidden');
            convertedList.innerHTML = '';
        }

        bindFileEvents();
        keepPickerAnchored();
        limitPrecheck();
    }

    /* Ранняя стена — спрашиваем «влезает ли очередь в сегодняшний бюджет» ПРИ
     * ИЗМЕНЕНИИ ОЧЕРЕДИ, а не при нажатии.
     *
     * Почему это появилось только 2026-08-09, вместе со снижением капа 25 → 3:
     * подсказка «осталось N из M» на этой странице живёт ВНУТРИ #actionBar,
     * который скрыт, пока не выбран файл, — то есть до первого файла её не
     * видно в принципе. Пока кап был 25, промах стоил дорого лишь тем, кто
     * ставил в очередь 26 треков. При капе 3 в стену упирается 22.9% активных
     * дней, и без этого вызова типичный сценарий выглядел бы так: человек
     * ставит альбом из 10 файлов, жмёт «Конвертировать», получает три готовых
     * и семь молча помеченных ошибкой. Ровно тот «мёртвый на ощупь» класс,
     * ради которого precheck и был написан (случай 2026-07-21 на /join).
     *
     * precheck сам показывает записку и гасит кнопку, когда очередь не влезает,
     * и снимает записку, когда влезает; очередь из уже посчитанных сегодня
     * файлов остаётся бесплатной и не блокируется. */
    function limitPrecheck() {
        try {
            if (!window.__toolLimit || !window.__toolLimit.precheck) return;
            var queued = files.filter(function (f) { return f.selected && f.status !== 'success'; })
                              .map(function (f) { return f.file; });
            window.__toolLimit.precheck('convert', queued);
        } catch (_) {}
    }
    /* Гонку «файлы упали раньше снимка тиров» здесь держал свой слушатель
     * tier-bootstrap:ready. С 2026-08-09 её закрывает САМ примитив
     * (tool-usage-limit.js: pendingPrecheck + replayPendingPrecheck), потому
     * что прогон показал — теряют предупреждение четверо из пяти вызывающих,
     * а не только этот тул. Локальную копию убрал: дублировать общий
     * примитив — ровно тот антипаттерн, из-за которого правки расходятся. */

    function bindFileEvents() {
        Array.prototype.forEach.call(fileList.querySelectorAll('.file-remove'), function (btn) {
            btn.onclick = function (e) { e.stopPropagation(); removeFile(+btn.dataset.id); };
        });
        Array.prototype.forEach.call(document.querySelectorAll('.play-btn'), function (btn) {
            btn.onclick = function (e) { e.stopPropagation(); togglePlay(+btn.dataset.id); };
        });
        Array.prototype.forEach.call(fileList.querySelectorAll('.file-target'), function (btn) {
            btn.onclick = function () { prepareFormatSelector(+btn.dataset.id); };
        });
        var convertedList = $('convertedList');
        if (convertedList) {
            Array.prototype.forEach.call(convertedList.querySelectorAll('.file-download-btn'), function (btn) {
                btn.onclick = function () { downloadOne(+btn.dataset.id); };
            });
            /* «Сделать громче»: ссылка остаётся ссылкой, а готовый файл едет в
               «Громкость» без повторной загрузки — та же дверь, что у /repair-audio. */
            Array.prototype.forEach.call(convertedList.querySelectorAll('a.cv-door'), function (a) {
                var f = files.find(function (x) { return x.id === +a.dataset.id; });
                if (!f) return;
                if (window.__toolHandoff && window.__toolHandoff.carry) {
                    window.__toolHandoff.carry(a, function () {
                        return f.jobBlob ? { blob: f.jobBlob, filename: f.outputName || (baseName(f.name) + '.' + outExtFor(f.outputFormat || globalFormat)) } : null;
                    });
                }
                a.addEventListener('click', function () {
                    aeF('faint_door', { to: 'volume', need: f.sound && f.sound.faint ? f.sound.faint.needDb : null });
                });
            });
        }
        var cAll = $('convertedDownloadAllBtn');
        var cZip = $('convertedDownloadZipBtn');
        if (cAll) cAll.onclick = downloadAll;
        if (cZip) cZip.onclick = downloadZip;
    }

    function downloadOne(fileId) {
        var f = files.find(function (x) { return x.id === fileId; });
        if (!f) return;
        var name = f.outputName || (baseName(f.name) + '.' + outExtFor(f.target || globalFormat));
        if (f.jobBlob && window.__downloadFile) {
            window.__downloadFile(f.jobBlob, name);
            aeF('file_download', { fmt: f.outputFormat || '' });
        } else if (f.jobBlob) {
            var a = document.createElement('a');
            a.href = f.jobUrl; a.download = name;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
        } else if (f.jobUrl) {
            // server-converted: download route, through the link door so it
            // spends this press's one download and is counted as a delivery
            if (window.__downloadLink) window.__downloadLink(f.jobUrl, name);
            else window.location.href = f.jobUrl;
        }
    }

    // ========== Output plan — ОДИН ответ на вопрос «что именно уедет на выход» ==========
    /*
     * outputPlan(f, fmt, settings) — свести источник (f.src) и запрошенные
     * настройки в спецификацию РЕЗУЛЬТАТА. Всё, что раньше угадывалось по
     * расширению, по «auto/не-auto» или по пустому полю, спрашивается здесь.
     *
     * Отвечает ровно на четыре вопроса, и каждый из них раньше имел свой
     * самодельный суррогат (перечислены в комментарии к probeHeader):
     *   rate/channels/bits — какой файл получится;
     *   isNoOp             — получится ли ПОБИТОВО то же самое (только тогда
     *                        конвертировать действительно незачем);
     *   changes            — что именно меняется (для текста и телеметрии);
     *   known              — есть ли у нас факты об источнике вообще.
     *
     * `known:false` — законный случай (формат, который AudioHeaderProbe не
     * разбирает). Тогда план НЕ выдумывает источник: isNoOp остаётся false, и
     * тул ведёт себя как раньше. Догадка хуже отсутствия ответа — ровно на этом
     * обожглись все четыре прежних суррогата.
     */
    /* Частота, которую формат ФИЗИЧЕСКИ может унести.
     *
     * Правило живёт в window.__audioCodecLimits (timbrica-core.js) — там же, где
     * его читают lite-лейн и остальные. Здесь остаётся только запасной путь на
     * случай, когда ядро почему-то не доехало: молча вернуть `want` было бы
     * хуже, чем посчитать по данным, которые у конвертера всё равно есть. */
    function rateForFormat(fmt, want) {
        var acl = window.__audioCodecLimits;
        if (acl && typeof acl.rateFor === 'function' && acl.ratesFor(fmt)) {
            return acl.rateFor(fmt, want);
        }
        var info = FORMAT_DATA[fmt] || {};
        if (info.fixedSampleRate) return parseInt(info.fixedSampleRate, 10);
        if (!want) return 0;
        var allowed = (info.sampleRates || []).map(Number).filter(function (n) { return n > 0; });
        if (!allowed.length) return want;                 // формат несёт любую
        if (allowed.indexOf(want) !== -1) return want;
        var below = allowed.filter(function (n) { return n < want; });
        return below.length ? Math.max.apply(null, below) : Math.min.apply(null, allowed);
    }

    function outputPlan(f, fmt, settings) {
        fmt = (fmt || '').toLowerCase();
        var info = FORMAT_DATA[fmt] || {};
        var src = f && f.src ? f.src : null;
        var s = settings || {};

        var rate;
        if (info.fixedSampleRate) rate = parseInt(info.fixedSampleRate, 10);
        else if (s.sampleRate && s.sampleRate !== 'auto') rate = parseInt(s.sampleRate, 10);
        else rate = src ? src.sampleRate : 0;             // 0 = «как в источнике, а он неизвестен»
        rate = rateForFormat(fmt, rate);

        var ch;
        if (info.channels) ch = parseInt(info.channels, 10);
        else if (s.channels && s.channels !== 'auto') ch = parseInt(s.channels, 10);
        else ch = src ? src.channels : 0;
        /* Потолок каналов — из реестра, а не из своего списка.
         *
         * ⚠️ Здесь стоял локальный MAX_STEREO (7 форматов). В реестре потолок 2
         * канала стоит у СЕМНАДЦАТИ, и разошлись они молча: convert не знал про
         * opus, webm, wma, m4r, mp2, cdda, 3gp, mmf, voc, 8svx. Для плана это
         * значило «на выходе 6 каналов» там, где кодек физически отдаёт 2 —
         * втрое завышенный прогноз размера, а после того как lite-лейн стал
         * рендерить план, ещё и попытка отдать энкодеру шесть каналов.
         * MAX_STEREO ниже остаётся только запасным путём для страниц без ядра. */
        var chCap = 0;
        try {
            if (window.__audioCodecLimits) chCap = window.__audioCodecLimits.maxChannels(fmt) || 0;
        } catch (_) {}
        if (!chCap && MAX_STEREO.indexOf(fmt) !== -1) chCap = 2;
        if (chCap && ch > chCap) ch = chCap;

        // Глубина осмысленна только там, где мы правда пишем PCM-глубину;
        // у остальных «битрейт» — это kbps, и к глубине отношения не имеет.
        var isDepthFormat = ['wav', 'flac', 'aiff', 'aif'].indexOf(fmt) !== -1;
        var bits = 0;
        if (isDepthFormat) {
            var b = parseInt(s.bitrate, 10);
            bits = (s.bitrate && s.bitrate !== 'auto' && s.bitrate !== 'source' && isFinite(b)) ? b : (src ? src.bits : 0);
            // «source» НЕ означает «как в источнике»: у бесплатного тира опция
            // остаётся выбираемой, но кодирование всё равно режется потолком
            // (решение Фарида 2026-07-19 «cap source too», применяет
            // QualityModule.getArgs). План обязан говорить о ФАКТИЧЕСКОМ
            // выходе, иначе предсказанный размер соврёт ровно на этой ветке,
            // а лейн уедет в FFmpeg ради глубины, которой не будет.
            try {
                if (window.__qualityTier && bits) bits = window.__qualityTier.clamp('bits', bits);
            } catch (_) {}
        }

        var sameContainer = !!(f && f.ext && outExtFor(fmt) === outExtFor(f.ext.toLowerCase()));
        var changes = [];
        if (src) {
            if (rate && rate !== src.sampleRate) changes.push('rate');
            if (ch && ch !== src.channels) changes.push('channels');
            if (isDepthFormat && bits && src.bits && bits !== src.bits) changes.push('bits');
        }
        if (!sameContainer) changes.push('container');

        return {
            rate: rate, channels: ch, bits: bits,
            known: !!src, sameContainer: sameContainer, changes: changes,
            lossless: !!info.noBitrate,
            /* Побитовый no-op возможен ТОЛЬКО у LOSSLESS: тара та же, источник
             * известен, ни один параметр не поехал — тогда перекодирование
             * действительно вернуло бы те же сэмплы.
             *
             * ⚠️ Для LOSSY этого не бывает никогда, и на этом я поймал сам себя.
             * Первая версия проверки не смотрела на lossless, и после того как
             * реестр научился 8 кГц (mp3 умеет MPEG-2.5), реальная фикстура
             * join-mp3-8khz.mp3 → mp3 на «Авто» дала совпадение по частоте и
             * каналам, план объявил no-op, и тул ОТКАЗАЛСЯ конвертировать —
             * хотя человек менял битрейт. Битрейт источника мы не знаем
             * (AudioHeaderProbe его не даёт), поэтому у lossy «то же самое»
             * недоказуемо в принципе: там всегда мягкое предупреждение со
             * вторым кликом, как и было до всей этой правки. */
            isNoOp: !!src && sameContainer && changes.length === 0 && !!info.noBitrate,
            durationSec: (src && src.durationSec) || (f && f.duration) || 0,
            bitrateKbps: (!info.noBitrate && s.bitrate) ? parseFloat(s.bitrate) : 0
        };
    }

    /* Предсказанный размер результата: { bytes, exact } или null.
     *
     * Три разных случая, и врать ни в одном нельзя — обещанный размер, который
     * не сойдётся, хуже отсутствия обещания:
     *   PCM (wav/aiff/cdda)  — считается формулой ТОЧНО;
     *   кодек с битрейтом    — битрейт × длительность, тоже точно;
     *   сжатый lossless      — степень сжатия зависит от материала и формулой
     *                          не берётся. НО если тара та же (flac → flac),
     *                          доля сжатия у того же материала сохраняется, и
     *                          размер источника можно масштабировать по
     *                          изменению частоты/каналов/глубины. Это и есть
     *                          случай «понизить 192 → 96 и сверить объём».
     *                          Чужой источник → честное «≈» от PCM.
     *
     * ЗАМЕРЕНО 2026-08-13, и замер развалил половину формулы — поэтому здесь
     * написано, ЧТО именно проверено, а не «формула работает»:
     *
     *   ЧАСТОТА и КАНАЛЫ масштабируются линейно. Широкополосный материал
     *   (розовый шум + тоны), FLAC 192 кГц → 96 / 48 / 44.1 / 32 при неизменной
     *   глубине: ошибка +0.2 %, −1.0 %, −1.2 %, −2.5 %. Этому можно верить.
     *   Остаточная погрешность зависит от СОДЕРЖИМОГО, а не от размера файла, и
     *   это проверено разделяющим замером (44.1 → 32 кГц):
     *       чистый синус  63 КБ → −19.3 %,  1.6 МБ → −18.8 %
     *       музыкоподобный 269 КБ → −0.8 %, 7.8 МБ → +0.4 %
     *   ⚠️ То есть ставить порог по размеру («не показывать для мелких файлов»)
     *   бессмысленно — он не отделяет плохой случай от хорошего. Плохой случай —
     *   чистый тон, а его на конвертер форматов не носят.
     *
     *   ГЛУБИНА — НЕТ. Наивное bits_new/bits_old даёт 0.667 для 24 → 16, а
     *   измеренная доля составила 0.576 на музыкоподобном материале и 0.287 на
     *   свипе. Причина понятна: младшие биты 24-битной записи — это почти шум,
     *   он несжимаем и стоит непропорционально дорого, а сколько именно его
     *   там — свойство материала, а не арифметики. Разброс вдвое; предсказать
     *   нельзя.
     *
     * Отсюда правило: у СЖАТОГО lossless прогноз показываем, только пока
     * глубина не меняется. У PCM глубина считается точно (размер не зависит от
     * содержимого — проверено: 192/24 → WAV 96k/16 предсказано 2.2 МБ, получено
     * 2.20 МБ), поэтому там ограничения нет.
     * ⚠️ Не «улучшать» это, добавив коэффициент для глубины: подогнанный по
     * одному файлу множитель промахнётся на другом, а число на экране выглядит
     * одинаково уверенно в обоих случаях.
     */
    var PCM_CONTAINERS = ['wav', 'wave', 'aiff', 'aif', 'cdda', 'raw'];
    function predictSize(f, plan) {
        if (!plan.rate) return null;
        var ch = plan.channels || 0;
        var dur = plan.durationSec;
        var fmt = (f.target || globalFormat).toLowerCase();

        if (plan.lossless && PCM_CONTAINERS.indexOf(fmt) !== -1) {
            if (!dur || !ch) return null;
            return { bytes: Math.round(dur * plan.rate * ch * ((plan.bits || 16) / 8)), exact: true };
        }
        if (!plan.lossless && plan.bitrateKbps) {
            if (!dur) return null;
            return { bytes: Math.round(dur * plan.bitrateKbps * 1000 / 8), exact: true };
        }
        // Сжатый lossless в ту же тару: масштабируем размер источника, но ТОЛЬКО
        // по проверенным осям. Меняется глубина — молчим (см. замер выше).
        if (plan.lossless && plan.known && plan.sameContainer && f.size) {
            var s = f.src;
            var depthChanges = !!(plan.bits && s.bits && plan.bits !== s.bits);
            if (!depthChanges) {
                var ratio = (plan.rate / s.sampleRate) * ((ch || s.channels) / (s.channels || 1));
                if (isFinite(ratio) && ratio > 0) return { bytes: Math.round(f.size * ratio), exact: false };
            }
        }
        return null;
    }

    // ========== Conversion args ==========
    // Container flag must survive every branch — losing it was the cdda/raw/
    // alac/snd root cause. Split codec map args into {container, codec} parts,
    // then reassemble: container + (bit-depth override | codec part) + rate/ch.
    function splitCodecArgs(fmt) {
        var raw = (FFMPEG_CODECS[fmt] || []).slice();
        var container = [], codec = [];
        for (var i = 0; i < raw.length; i += 2) {
            var k = raw[i], v = raw[i + 1];
            if (k === '-f') container = ['-f', v];
            else if (v === undefined) codec.push(k);
            else codec.push(k, v);
        }
        return { container: container, codec: codec };
    }

    function buildArgs(fmt, bitrate, sampleRate, channels, sourceChannels, plan) {
        var info = FORMAT_DATA[fmt] || {};
        var parts = splitCodecArgs(fmt);
        var args = parts.container.slice();

        var isDepthFormat = ['wav', 'flac', 'aiff', 'aif'].indexOf(fmt) !== -1;
        var qmLive = window.QualityModule && !window.QualityModule.__isStub && typeof QualityModule.argsFor === 'function';
        /* Разрядность выхода — у ПЛАНА, и теперь её слышит энкодер.
         *
         * ⚠️ Здесь стояло `&& bitrate !== 'source'`, то есть «Исходное» уезжало
         * мимо QualityModule в статическую карту кодеков — а она про глубину не
         * знает ничего и говорит одно и то же всегда. ЗАМЕРЕНО 2026-09-03 на
         * живой странице (`convert-bitdepth-debug`), оба направления сразу:
         *
         *   Премиум · FLAC 24 бита → WAV · «Исходное»  → pcm_s16le, 16 бит.
         *       Платящий терял восемь бит через опцию, которая обещает их
         *       сохранить; соседняя «24-бит» при этом отрабатывала верно.
         *   Гость   · FLAC 24 бита → FLAC · «Исходное» → 24 бита НАСТОЯЩИХ.
         *       У flac в карте нет `-sample_fmt`, и кодек наследовал глубину
         *       входа — платная ось раздавалась даром ОДНОЙ строкой выше
         *       опции «24-бит · Премиум», на том же экране.
         *
         * То есть один и тот же выбор значил противоположное в зависимости от
         * формата вывода, и оба значения расходились с подписью. Корень — два
         * владельца одного числа: buildPlan считал глубину (для оценки размера
         * и для выбора лейна — liteBlockedReason смотрит plan.bits), а
         * buildArgs её не спрашивал. Теперь спрашивает: план — единственный
         * источник, а QualityModule — единственный переводчик числа в аргументы
         * (он же повторно применит потолок тира, это идемпотентно).
         *
         * plan.bits === 0 значит «фактов о источнике нет» (неизвестный
         * контейнер): тогда прежнее поведение — умолчание карты кодеков. */
        var wantBits = (plan && plan.bits > 0) ? plan.bits : 0;
        if (info.noBitrate && isDepthFormat && qmLive && wantBits) {
            var qmArgs = QualityModule.argsFor(fmt, String(wantBits));
            args = args.concat(qmArgs && qmArgs.length ? qmArgs : parts.codec);
        } else {
            args = args.concat(parts.codec);
        }
        /* Треугольный дизеринг там, где FFmpeg уменьшает разрядность. Без этой
         * опции swresample просто отрезает младшие биты: 24-битный файл в
         * 16 бит выходил со ступенчатым искажением тихих мест и смещением в
         * полшага (замер 28.10, см. liteBlockedReason). Опция касается только
         * пересчёта в целые меньшей разрядности — 24 → 24, float-кодеки и
         * копия потока её не замечают. */
        if (isDepthFormat || fmt === 'alac') args.push('-dither_method', 'triangular');

        if (!info.noBitrate && bitrate) {
            // vorbis needs -q:a (fails with -b:a on some inputs); the shared
            // processor's quality map knows which formats that applies to.
            var qMap = (window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.qualityMap) || {};
            if (qMap[fmt]) {
                var kbps = parseInt(bitrate, 10);
                var qv = qMap[fmt][kbps];
                if (qv === undefined) qv = Math.round((kbps - 32) / 36);
                qv = Math.max(0, Math.min(10, qv));
                args.push('-q:a', String(qv));
            } else {
                args.push('-b:a', bitrate + 'k');
            }
        }

        /* Частоту передаём ВСЕГДА, когда её знаем, — включая «Авто».
         *
         * ⚠️ Раньше при «Авто» `-ar` не передавался вовсе, и это читалось как
         * «сохранить частоту источника». Молчание — не намерение: общий
         * FFmpegProcessor.process трактует отсутствие `-ar` у mp3 как «вызывающему
         * всё равно» и ПОДСТАВЛЯЕТ СВОИ 44100 (строка `if(_outExt==="mp3" &&
         * o.indexOf("-ar")===-1 …) o.concat(["-ar","44100"])`). ЗАМЕРЕНО
         * 2026-08-13: 5.1 WAV 48 кГц → mp3 на «Авто» приезжал 44.1 кГц, тогда как
         * тот же файл в стерео шёл по lite-лейну и сохранял 48 кГц. То есть
         * «Авто» означало разное на двух лейнах — ровно то расхождение, о котором
         * предупреждает комментарий в audio-native.js («a 48 kHz master ended up
         * at 44.1 kHz on some paths and not others»).
         *
         * Умолчание процессора не трогаем: оно страхует десятки других тулов,
         * которые про источник действительно ничего не знают. Чинится сторона
         * намерения — /convert теперь ГОВОРИТ, чего хочет. */
        /* Значение ПЛАНА — первым: план зажал частоту под формат ЭТОГО файла
         * (rateForFormat), а сырое значение селекта подписано под общий формат —
         * у файла с форматом из чипа 96 кГц FLAC-селекта уезжали в MP3 как
         * `-ar 96000`. Для общего формата они совпадают: селект строится из
         * лестницы самого формата. */
        var wantRate = plan && plan.rate ? plan.rate : 0;
        if (info.fixedSampleRate) {
            args.push('-ar', String(info.fixedSampleRate));
        } else if (wantRate) {
            args.push('-ar', String(wantRate));
        } else if (sampleRate && sampleRate !== 'auto') {
            args.push('-ar', String(sampleRate));
        }

        /* Каналы — та же дисциплина, что и разрядность: число считает ПЛАН
         * (он клампит по реестру __audioCodecLimits), а здесь оно только
         * переводится в аргумент. Раньше сюда уезжало СЫРОЕ значение селектора,
         * и для десяти форматов с потолком 2 канала это означало `-ac 6` при
         * кодеке, который шесть не берёт: план говорил одно, энкодер получал
         * другое — тот же разрыв двух владельцев, что и у глубины.
         *
         * ⚠️ АПМИКС ИДЁТ НЕ ЧЕРЕЗ `-ac`. Голый `-ac 6` из стерео дописывает
         * четыре ПУСТЫХ канала (замерено 2026-09-03: FC/LFE/BL/BR в тишине,
         * у 7.1 пустых шесть из восьми). Настоящие каналы синтезирует матрица
         * UpmixModule: снятие центра, LFE с фильтром, тылы с задержкой Хааса.
         * Свой -af этот сборщик не строит, поэтому preFilter не нужен. */
        var up = upmixFor(plan);
        if (up && window.UpmixModule && !window.UpmixModule.__isStub) {
            args = args.concat(UpmixModule.ffmpegArgs(up));
            return args;
        }

        var hasAc = args.indexOf('-ac') !== -1;
        if (!hasAc) {
            var wantCh = (plan && plan.channels > 0) ? plan.channels : 0;
            if (channels && channels !== 'auto') {
                args.push('-ac', String(wantCh || channels));
            } else if (wantCh && sourceChannels && wantCh !== sourceChannels) {
                // «Авто» + кодек уже потолка: даунмикс называем вслух, а не
                // оставляем энкодеру, который на 5.1 просто падает.
                args.push('-ac', String(wantCh));
            } else if (MAX_STEREO.indexOf(fmt) !== -1 && sourceChannels > 2) {
                args.push('-ac', '2'); // запасной путь для страниц без реестра
            }
        }
        return args;
    }

    // ========== Lite path (MediaPipeline: no WASM) ==========
    function liteEligible(sourceExt, fmt) {
        if (['mp3', 'wav', 'flac'].indexOf(fmt) === -1) return false;
        return WEBAUDIO_DECODABLE.indexOf(sourceExt) !== -1;
    }

    /* «Файл → PCM» — одна дверь сайта, AudioDecoderRobust: у mp4-семейства
     * (m4a/mp4/mov, и дорожка, вынутая из видео) — WebCodecs с частотой по
     * часам контейнера, потом браузерный декодер, потом FFmpeg.
     *
     * До 2026-10-08 здесь стоял голый decodeAudioData, который правило дома
     * для mp4 запрещает. Замер на /convert в настоящем Yandex Browser:
     * HE-AAC v2 (m4a и mp4) → MP3/WAV выходил ВДВОЕ длиннее (26,9 с из 13,5)
     * и на октаву ниже (центроид 1724 Гц против 3627), без единой ошибки;
     * Chromium читал верно. Попутно выход перестал зависеть от частоты
     * контекста: дорожка mp4 приходит на своей частоте.
     *
     * `gated: true` — память судит сам /convert ДО декода (liteBlockedReason
     * уводит тяжёлый файл в потоковый FFmpeg-лейн); диалог «разделить на
     * части» посреди пачки не нужен. */
    function decodeInBrowser(file) {
        var R = window.AudioDecoderRobust;
        if (R && !R.__isStub && typeof R.robust === 'function') {
            var Ctor0 = window.AudioContext || window.webkitAudioContext;
            var ctx0 = new Ctor0();
            var done = function () { try { ctx0.close(); } catch (e) {} };
            return R.robust(file, ctx0, { slug: 'convert', gated: true }).then(function (r) {
                done();
                if (!r || !r.buffer) throw new Error('decode returned no audio');
                // Which decoder answered — the level verdict below reports it.
                try { r.buffer._decodeTier = r.tier; } catch (_) {}
                return r.buffer;
            }, function (err) { done(); throw err; });
        }
        // The page without the shared decoder (a blocker stripped it): plain
        // WebAudio. The global decodeAudioData patch would chain into
        // FFmpegProcessor on failure — pointless here (we're the caller
        // deciding fallbacks), so suppress for the duration.
        return file.arrayBuffer().then(function (buf) {
            var Ctor = window.AudioContext || window.webkitAudioContext;
            var ctx = new Ctor();
            window.__suppressDecodeFallback = true;
            return ctx.decodeAudioData(buf).then(function (decoded) {
                window.__suppressDecodeFallback = false;
                try { ctx.close(); } catch (e) {}
                return decoded;
            }, function (err) {
                window.__suppressDecodeFallback = false;
                try { ctx.close(); } catch (e) {}
                throw err;
            });
        });
    }

    function resampleBuffer(buf, targetSr, targetCh) {
        if (targetSr === buf.sampleRate && targetCh === buf.numberOfChannels) return Promise.resolve(buf);
        var off = new OfflineAudioContext(targetCh, Math.ceil(buf.duration * targetSr), targetSr);
        var src = off.createBufferSource();
        src.buffer = buf; src.connect(off.destination); src.start();
        return off.startRendering();
    }

    /* Насколько громок звук, который уходит в файл, — и тихий ли он для слушателя.
     *
     * Жалоба 18.10.2026 (Android 13, Яндекс 26.8): «конвертировал, слушаю, а звук
     * стал нереально тихим». Сквозной прогон на том же браузере с записью того же
     * рода (моно AAC-LC 44,1 кГц на потолке битрейта, кодировщик и контейнер
     * Android) дал MP3 на −22,88 дБ RMS против −22,44 у исходника: громкость лейн не
     * теряет. А сказать заявителю, тихой ли была САМА запись, было нечем — уровень
     * результата нигде не записывался. Теперь он есть у каждого файла этого лейна.
     *
     * Меряет TakeLevel, решает SoundVerdict — те же владельцы и пороги, что у
     * /repair-audio («звук есть, но очень тихий»: громким местам до обычного
     * уровня не хватает ≥ 18 дБ). Громкость конвертер не трогает: он говорит, что
     * запись тихая, и даёт дверь «Сделать громче» с этим файлом. Меряется готовый
     * буфер (после частоты и каналов), то есть ровно то, что будет в файле. */
    function soundLevel(prepared, decoded, f, fmt) {
        var SV = window.SoundVerdict;
        if (!SV || typeof SV.fromBuffer !== 'function' || !prepared) return null;
        var facts = null, v = null;
        try { facts = SV.fromBuffer(prepared); } catch (_) { facts = null; }
        if (!facts || !facts.level) return null;
        var tier = decoded && decoded._decodeTier;
        try { v = SV.judge({ level: facts.level, stereo: facts.stereo, evidence: 'decode', tier: tier }); } catch (_) { v = null; }
        if (!v) return null;
        var faint = v.findings.filter(function (x) { return x.id === 'faint'; })[0] || null;
        var r1 = function (x) { return (typeof x === 'number' && isFinite(x)) ? Math.round(x * 10) / 10 : null; };
        aeF('result_level', {
            peak: r1(facts.level.peakDb), level: r1(facts.level.levelDb), need: faint ? faint.needDb : null,
            state: v.state, ids: v.findings.map(function (x) { return x.id; }).join(','),
            ch: prepared.numberOfChannels, srcCh: decoded ? decoded.numberOfChannels : 0, sr: prepared.sampleRate,
            tier: (typeof tier === 'number') ? tier : null, ext: f.ext, fmt: fmt,
            dur: Math.round(prepared.duration || 0)
        });
        return { state: v.state, peakDb: facts.level.peakDb, levelDb: facts.level.levelDb, faint: faint };
    }

    /* Частота AudioContext'а — та, к которой decodeAudioData ПРИВЕДЁТ любой вход.
       Считаем один раз: контекст нужен только чтобы спросить число. */
    var _ctxRate = 0;
    function contextRate() {
        if (_ctxRate) return _ctxRate;
        try {
            var C = window.AudioContext || window.webkitAudioContext;
            var c = new C();
            _ctxRate = c.sampleRate || 48000;
            try { c.close(); } catch (e) {}
        } catch (e) { _ctxRate = 48000; }
        return _ctxRate;
    }

    /* Досмотр ДО декода — решает, можно ли этому файлу в lite-путь.
     *
     * Зачем: ниже по коду уже был гейт памяти, но он считал пик ПОСЛЕ
     * decodeAudioData — то есть после той самой аллокации, от которой защищал.
     * Пока /convert резал всё крупнее 100 МБ, это не стреляло; со снятием
     * стены (2026-08-09) в lite-путь пошли файлы в сотни мегабайт, и порядок
     * стал важен: decodeInBrowser держит ОДНОВРЕМЕННО ArrayBuffer всего файла
     * и раскодированный Float32. AudioHeaderProbe уже загружен на этой
     * странице, читает 64 КБ и отвечает за ~8 мс — этого хватает, чтобы
     * посчитать оба слагаемых заранее и увести тяжёлый файл в потоковый
     * FFmpeg-лейн (замер: 448 МБ FLAC 24/192 → MP3 проходит там за 118 с при
     * пике кучи 497 МБ, `path:"stream"`).
     *
     * Второе, что видно только из заголовка: lite-путь физически не может
     * отдать hi-res, и обе половины этого проверены, а не предположены.
     *   ЧАСТОТА — decodeAudioData отдаёт звук на частоте КОНТЕКСТА: замер на
     *     24/192 FLAC вернул буфер с sampleRate 48000.
     *   РАЗРЯДНОСТЬ — media-pipeline/audio-native.js собирает WAV в Int16Array,
     *     а над веткой FLAC там же стоит «FLAC accepts only 16-bit PCM input
     *     via WebCodecs». То есть 24 бита теряются всегда.
     * FFmpeg же при 'auto' не подставляет `-ar` вовсе и сохраняет частоту, а
     * без `-sample_fmt` наследует разрядность входа (проверено: 24-битный FLAC
     * на входе → s32/24 бита на выходе).
     *
     * ⚠️ ЧЕМ ЭТА ПРОВЕРКА БЫЛА РАНЬШЕ И ПОЧЕМУ ЭТО НЕ ЛОВИЛОСЬ.
     * Обе строки стояли под условием `keepSource` — «пользователь оставил Hz на
     * auto». Условие подменяло вопрос «что уедет на выход» вопросом «как выглядит
     * контрол», и совпадало с истиной только пока частоту не трогали. Стоило
     * выбрать 96k явно — keepSource становился false, гейт молчал, и hi-res
     * уходил в lite-путь. ЗАМЕРЕНО на этом коде (192 кГц/24 бита свип → WAV
     * 96k): контейнер честно писал 96000 Гц, а в полосе 36-45 кГц вместо
     * −10 дБ было −79 дБ, то есть содержимого выше 24 кГц не осталось вовсе,
     * и 24 бита стали 16. Тот самый тихий downgrade, ради запрета которого весь
     * этот комментарий и написан.
     *
     * Теперь сравнивается ПЛАН с физическим потолком лейна, а не настройка с
     * ожидаемым значением. Утверждение «lite отдаёт не выше частоты контекста и
     * не глубже 16 бит» — свойство самого лейна: оно не протухнет от того, что
     * в UI появится новая опция.
     *
     * Возвращает null|reason-строку (null = lite можно). Синхронная: факты уже
     * прочитаны один раз в probeHeader, второй раз файл не трогаем.
     */
    function liteBlockedReason(f, plan) {
        var src = f.src;
        if (!src) return null; // формат без заголовочных фактов — прежнее поведение

        // Честность лейна. Планируемая частота выше той, которую lite физически
        // способен выдать → он отдаст ПОДДЕЛКУ с правильным числом в заголовке.
        if (plan.rate && plan.rate > contextRate()) return 'hires_rate';
        if (plan.bits && plan.bits > 16) return 'hires_depth';
        /* Разрядность УМЕНЬШАЕТСЯ (24 → 16 у гостя на «Исходном», 24 → 16 по
         * выбору) — нужен дизеринг, а lite его не умеет: audio-native.js
         * кладёт float в Int16Array отбрасыванием дробной части. Замер 28.10
         * на синусе −118 dBFS: без дизеринга выход — только −1 и 0 со
         * смещением в полшага (искажение, привязанное к сигналу, — слышно в
         * тихих концовках), с треугольным — −1/0/+1 без смещения. FFmpeg-лейн
         * дизерит (`-dither_method` в buildArgs). */
        if (plan.bits && src.bits && plan.bits < src.bits) return 'depth_dither';
        /* Апмикс lite-лейну не по силам: он ресемплит через OfflineAudioContext,
         * а тот при расширении стерео до 5.1 раскладывает L/R по фронтам и
         * оставляет остальное пустым — ровно та подделка, которую матрица и
         * заменяет. Отправляем в FFmpeg, как это делает /audio-compressor. */
        if (upmixFor(plan)) return 'upmix';

        if (!src.durationSec) return null; // mp3 и прочие без счётчика кадров
        // Кадры ПОСЛЕ ресемплинга к частоте контекста — именно столько
        // Float32 и будет в памяти.
        var frames = src.durationSec * contextRate();
        var pcmBytes = frames * (src.channels || 2) * 4;
        // decode + resample + энкодер держат ~3 копии (та же тройка, что и
        // в прежней проверке), плюс ArrayBuffer самого файла.
        var peakBytes = pcmBytes * 3 + (f.size || 0);
        var dm = (navigator.deviceMemory || 4) * 1024 * 1024 * 1024;
        if (peakBytes > dm * 0.25) return 'mem';
        return null;
    }

    function convertFileLite(f, fmt, bitrate, sampleRate, channels, plan) {
        var w = workOf(f);
        if (!liteEligible(w.ext, fmt)) return Promise.resolve(null);
        if (!window.ensureMediaPipeline) return Promise.resolve(null);
        var flacDepth = (fmt === 'flac' && bitrate && bitrate !== 'source' && parseInt(bitrate, 10) > 16);
        if (flacDepth) return Promise.resolve(null); // 24-bit flac needs FFmpeg's sample_fmt args
        var blocked = liteBlockedReason(f, plan);
        if (blocked) {
            aeF('pipeline_route', { pipeline: 'ffmpeg', reason: 'lite_skipped_' + blocked, fileMB: Math.round((f.size || 0) / 1048576) });
            return Promise.resolve(null);
        }
        return window.ensureMediaPipeline().then(function (ready) {
            if (!ready || !window.MediaPipeline) return null;
            var supPromise = Promise.resolve(true);
            if (fmt === 'flac') {
                if (typeof AudioEncoder === 'undefined') return null;
                supPromise = AudioEncoder.isConfigSupported({ codec: 'flac', sampleRate: 48000, numberOfChannels: 2, bitrate: 192000 })
                    .then(function (s) { return !!(s && s.supported); }, function () { return false; });
            }
            return supPromise.then(function (sup) {
                if (!sup) return null;
                return decodeInBrowser(w.file).then(function (buf) {
                    /* Лейн ИСПОЛНЯЕТ план, а не пересчитывает его заново.
                     *
                     * ⚠️ Раньше при «Авто» здесь стояло `buf.sampleRate` — то
                     * есть частота КОНТЕКСТА, к которой decodeAudioData привёл
                     * вход, а вовсе не частота источника. ЗАМЕРЕНО на реальной
                     * фикстуре join-mp3-8khz.mp3: голосовой файл 8 кГц → mp3 на
                     * «Авто» приезжал 48 кГц, то есть тул раздувал речь в шесть
                     * раз и выдавал это за «как в источнике». План знает
                     * настоящие 8000 из заголовка — его и рендерим.
                     * Тот же зазор с другой стороны, что и hi-res: лейн говорил
                     * о себе, вместо того чтобы исполнять обещанное. */
                    var targetSr = (plan && plan.rate) ? plan.rate
                        : ((sampleRate && sampleRate !== 'auto') ? parseInt(sampleRate, 10) : buf.sampleRate);
                    var targetCh = (plan && plan.channels) ? plan.channels
                        : ((channels && channels !== 'auto') ? parseInt(channels, 10) : buf.numberOfChannels);
                    if (fmt === 'mp3' && targetCh > 2) targetCh = 2; // lame is stereo-max
                    var dm = (navigator.deviceMemory || 4) * 1024 * 1024 * 1024;
                    var peakBytes = Math.ceil(buf.duration * targetSr) * targetCh * 4 * 3;
                    if (peakBytes > dm * 0.25) return null;
                    return resampleBuffer(buf, targetSr, targetCh).then(function (prepared) {
                        var kbps = parseInt(bitrate, 10);
                        var sound = soundLevel(prepared, buf, f, fmt);
                        return window.MediaPipeline.audio.encode(prepared, fmt, { bitrate: isNaN(kbps) ? 192 : kbps, slug: 'convert' }).then(function (blob) {
                            if (!blob || blob.size === 0) return null;
                            aeF('pipeline_route', { pipeline: 'lite', reason: f.ext + '_to_' + fmt });
                            return {
                                name: baseName(f.name) + '.' + outExtFor(fmt),
                                blob: blob,
                                url: URL.createObjectURL(blob),
                                size: blob.size,
                                sound: sound,
                            };
                        });
                    });
                });
            });
        }).catch(function (e) {
            ae('feature_use', { slug: 'convert', action: 'lite_failed', reason: f.ext + '_to_' + fmt, err: ((e && e.message) || '').substring(0, 100) });
            return null;
        });
    }

    // ========== FFmpeg path (shared processor) ==========
    var ffmpegProgressWired = false;
    function ensureFFmpeg() {
        if (!window.FFmpegProcessor || window.FFmpegProcessor.__isStub) {
            return Promise.reject(new Error(t.browser_unavailable || 'Engine unavailable'));
        }
        return window.FFmpegProcessor.init(function (ratio) {
            // download progress 0..1 → first half of the bar during load
            if (progressBar && isConverting) progressBar.style.width = Math.round(ratio * 100) + '%';
        }).then(function () {
            if (!ffmpegProgressWired) {
                ffmpegProgressWired = true;
                window.FFmpegProcessor.onProgress(function (p) {
                    if (progressBar && isConverting) progressBar.style.width = Math.min(100, Math.round((p || 0) * 100)) + '%';
                });
            }
            return true;
        });
    }

    var DECODE_FAIL_RX = /time_base to value\s*[01]\/0|error configuring filter graph|error applying options to the filter|invalid data found|header processing|could not find codec parameters|error while decoding|invalid frame|does not contain any stream|moov atom not found/i;

    function convertFileFFmpeg(f, fmt, args, preInputArgs) {
        var outName = 'output.' + outExtFor(fmt);
        var w = workOf(f);
        // A video that still is a video here (its track did not come out without
        // loss): never let the picture into an audio output.
        if (w.video) args = ['-map', '0:a:' + (w.track || 0), '-vn', '-sn', '-dn'].concat(args);
        return window.FFmpegProcessor.process(w.file, args, outName, 'input.' + w.ext, preInputArgs || null)
            .then(function (r) {
                if (!r || !r.blob || r.blob.size === 0) throw new Error('Empty output for ' + f.name);
                return {
                    name: baseName(f.name) + '.' + outExtFor(fmt),
                    blob: r.blob,
                    url: r.url || URL.createObjectURL(r.blob),
                    size: r.blob.size,
                };
            });
    }

    // Bridge: FFmpeg couldn't decode, browser might (broken VBR mp3 headers
    // play fine in <audio> but hit `time_base 1/0` in lavfi). Decode with
    // WebAudio → WAV → FFmpeg encodes the clean PCM to the real target.
    function convertViaBridge(f, fmt, args, needReinit) {
        var w = workOf(f);
        if (WEBAUDIO_DECODABLE.indexOf(w.ext) === -1) return Promise.resolve(null);
        aeF('bridge_attempt', { ext: f.ext, to: fmt });
        // After a wasm runtime crash the instance is poisoned — writing the
        // bridge WAV into its FS would hang or rethrow. Rebuild first (core
        // comes from the blob cache, ~1-2 s, only on this rare path).
        var pre = Promise.resolve();
        if (needReinit && window.FFmpegProcessor.reinit) {
            pre = Promise.resolve(window.FFmpegProcessor.reinit()).then(function () { return window.FFmpegProcessor.init(); }).catch(function () {});
        }
        return pre.then(function () { return decodeInBrowser(w.file); }).then(function (buf) {
            var dm = (navigator.deviceMemory || 4) * 1024 * 1024 * 1024;
            if (buf.length * buf.numberOfChannels * 4 * 3 > dm * 0.3) return null;
            var wavBlob = null;
            if (window.MediaPipeline && window.MediaPipeline.audio && typeof window.MediaPipeline.audio.audioBufferToWav === 'function') {
                wavBlob = window.MediaPipeline.audio.audioBufferToWav(buf);
            } else {
                wavBlob = audioBufferToWavLocal(buf);
            }
            if (!wavBlob) return null;
            var wavFile = new File([wavBlob], 'bridge.wav', { type: 'audio/wav' });
            var sound = soundLevel(buf, buf, f, fmt);
            return window.FFmpegProcessor.process(wavFile, args, 'output.' + outExtFor(fmt), 'bridge.wav').then(function (r) {
                if (!r || !r.blob || r.blob.size === 0) return null;
                aeF('bridge_ok', { ext: f.ext, to: fmt });
                return {
                    name: baseName(f.name) + '.' + outExtFor(fmt),
                    blob: r.blob,
                    url: r.url || URL.createObjectURL(r.blob),
                    size: r.blob.size,
                    sound: sound,
                };
            });
        }).catch(function (e) {
            aeF('bridge_fail', { ext: f.ext, to: fmt, err: ((e && e.message) || '').substring(0, 80) });
            return null;
        });
    }

    function audioBufferToWavLocal(buf) {
        var numCh = buf.numberOfChannels, sr = buf.sampleRate, len = buf.length;
        var bytesPerSample = 2, blockAlign = numCh * bytesPerSample;
        var dataSize = len * blockAlign;
        var ab = new ArrayBuffer(44 + dataSize);
        var view = new DataView(ab);
        var writeStr = function (off, s) { for (var i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
        writeStr(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); writeStr(8, 'WAVE');
        writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
        view.setUint16(22, numCh, true); view.setUint32(24, sr, true);
        view.setUint32(28, sr * blockAlign, true); view.setUint16(32, blockAlign, true);
        view.setUint16(34, 16, true); writeStr(36, 'data'); view.setUint32(40, dataSize, true);
        // Готовый файл — общий квантователь с дизерингом (docs/why/pcm16-quantize.md).
        var pcm = new Int16Array(ab, 44, len * numCh), q = window.__pcm16Quantizer(true);
        for (var c = 0; c < numCh; c++) q(buf.getChannelData(c), len, pcm, c, numCh);
        return new Blob([ab], { type: 'audio/wav' });
    }

    // ========== Server path (legacy Go microservice) ==========
    function convertFileServer(f, fmt, bitrate, sampleRate) {
        var formData = new FormData();
        // the sound, not the video: the server lane gets the track (MBs, not GBs)
        formData.append('audio', workOf(f).file);
        formData.append('format', fmt);
        formData.append('_token', CFG.csrf);
        formData.append('bitrate', bitrate + 'k');
        if (sampleRate !== 'auto') formData.append('sample_rate', sampleRate);
        return fetch(CFG.runUrl, { method: 'POST', body: formData }).then(function (resp) {
            var contentType = resp.headers.get('content-type') || '';
            var parse = contentType.indexOf('application/json') !== -1
                ? resp.json().catch(function () { return null; })
                : resp.text().then(function (tx) { return tx ? { error: tx } : null; }, function () { return null; });
            return parse.then(function (data) {
                if (resp.redirected) return { url: resp.url, isServer: true };
                if (resp.ok && data && data.success && data.files && data.files.length > 0) {
                    return { url: data.files[0].url, isServer: true, serverData: data };
                }
                throw new Error((data && data.error) || ('Server conversion failed (' + resp.status + ')'));
            });
        });
    }

    // ========== Convert orchestration ==========
    function getSourceChannels(f) {
        // Раньше здесь стояло `f._srcChannels || 0` с пометкой «best effort;
        // filled by preview metadata when available». Поле не заполнялось
        // НИГДЕ — grep по файлу давал ровно два вхождения, объявление и это
        // чтение, — то есть авто-даунмикс 5.1 → mp3 (ради которого функция и
        // появилась) не срабатывал ни разу. Теперь берём прочитанный факт.
        return (f.src && f.src.channels) || 0;
    }

    function classifyAndLabel(err) {
        var msg = ((err && err.message) || '').toLowerCase();
        if (err && err.code === 'ENGINE_PRE_ABORTED') return t.browser_unavailable || 'Engine unavailable';
        if (err && err.code === 'no-audio') return t.no_audio_in_video || t.file_convert_failed || 'No audio track';
        // Decode-class first: "file is corrupted" beats the processor's generic
        // "processing error, try another format" for truncated/broken inputs.
        if (DECODE_FAIL_RX.test(msg)) return t.file_corrupted || 'File is corrupted or uses an unsupported codec';
        if (err && err.userMessage) return err.userMessage;
        if (msg.indexOf('memory') !== -1 || msg.indexOf('allocate') !== -1) return t.file_too_large_ram || 'File too large for available memory';
        if (msg.indexOf('file_lost') !== -1 || msg.indexOf('no longer accessible') !== -1) return t.file_lost || 'The file is no longer accessible. Please re-select it.';
        if (msg.indexOf('empty') !== -1 && msg.indexOf('0 bytes') !== -1) return t.empty_file || 'File is empty or unreadable (0 bytes).';
        return t.file_convert_failed || 'Conversion failed';
    }

    function convert() {
        if (isConverting) return;
        var toConvert = files.filter(function (f) { return f.supported && f.selected && f.status !== 'success'; });
        if (!toConvert.length) return;

        var fileSettings = {}, plans = {};
        toConvert.forEach(function (f) {
            fileSettings[f.id] = settingsFor(f);
            plans[f.id] = outputPlan(f, f.target || globalFormat, fileSettings[f.id]);
        });

        /* Стоит ли вообще запускать работу.
         *
         * ⚠️ ЧТО ЗДЕСЬ БЫЛО. Проверка сравнивала РАСШИРЕНИЕ источника с целевым
         * форматом и, если они совпадали у lossless-файла, наглухо отбивала
         * запуск: «Формат совпадает с исходным. Выберите другой формат.»
         * Комментарий над ней утверждал, что такой прогон — «pure no-op».
         * Для FLAC → FLAC при смене частоты, каналов или глубины это неправда:
         * работа есть, и именно за ней человек пришёл. Жалоба 2026-08-11
         * (192 → 96 кГц у владельца ресивера, который не переваривает выше 96)
         * упёрлась ровно сюда — понижение частоты было объявлено «тем же самым»
         * и запрещено, хотя контрол частоты стоит на той же панели.
         *
         * Теперь спрашивается план: no-op — это когда тара та же И НИ ОДИН
         * параметр не изменился. Пока источник неизвестен (plan.known === false),
         * утверждать «ничего не изменится» нельзя — такой файл идёт по мягкой
         * ветке с предупреждением, а не в тупик. */
        var noOp = toConvert.filter(function (f) { return plans[f.id].isNoOp; });
        if (noOp.length === toConvert.length) {
            toast(t.same_format_warning, 'warning');
            aeF('same_format_blocked', { reason: 'true_noop' });
            return;
        }
        var sameContainer = toConvert.filter(function (f) { return plans[f.id].sameContainer; });
        if (sameContainer.length === toConvert.length && !convert._sameFormatWarned) {
            // Тара та же, но что-то меняется. Для lossy это перекодирование с
            // потерей — предупреждаем один раз; для lossless потерь нет, но
            // сказать, ЧТО именно поедет, всё равно полезно.
            var anyLossy = sameContainer.some(function (f) { return !plans[f.id].lossless; });
            if (anyLossy) {
                convert._sameFormatWarned = true;
                toast(t.same_format_reencode || t.same_format_warning, 'warning');
                return; // second click proceeds
            }
        }
        convert._sameFormatWarned = false;

        var targets = {};
        toConvert.forEach(function (f) { targets[f.target || globalFormat] = 1; });
        var needsServer = Object.keys(targets).some(function (fmt) { return (FORMATS[fmt] || {}).mode === 'server'; });
        var needsBrowser = Object.keys(targets).some(function (fmt) { return (FORMATS[fmt] || {}).mode !== 'server'; });

        if (needsServer && toConvert.length > 1) {
            toast(t.server_single_error, 'error');
            return;
        }

        toConvert.forEach(function (f) { if (f.status === 'error') { f.status = 'pending'; f.errorReason = null; } });

        isConverting = true;
        convertCancelled = false;
        convertBtn.disabled = true;
        convertBtnText.textContent = t.converting;
        progressOverlay.classList.add('show');
        if (window.__announce) window.__announce(t.converting);
        progressBar.style.width = '0%';
        progressTitle.textContent = t.converting;

        var i = 0;
        var wasmUsed = false;

        function nextFile() {
            if (convertCancelled || i >= toConvert.length) return finish();
            var f = toConvert[i];

            /* Daily cap — config/tiers.php → daily_runs.convert (numbers live
               there, not here: this comment said «25 free» long after it was
               not). ONE TOKEN PER FILE: each conversion is an independent
               job whose result lands in the list on its own, so a wall reached
               mid-queue stops the rest instead of running them free — the files
               already converted stay downloadable.
               Billed on DELIVERY (f.status === 'success'), settled once per file
               in the chain's tail below. A cancel that still leaves a converted
               file in the list HAS delivered it and bills; files never started
               cost nothing. */
            var myRun = window.__toolLimit ? window.__toolLimit.begin('convert', window.__toolLimit.fileKey ? window.__toolLimit.fileKey(f.file) : null) : null;
            if (window.__toolLimit && !myRun) return finish();   // wall — note already shown

            var fmt = f.target || globalFormat;
            var fs = fileSettings[f.id] || settingsFor(f);
            var bitrate = fs.bitrate, sampleRate = fs.sampleRate, channels = fs.channels;
            f.status = 'processing';
            render();
            progressFile.textContent = t.processing + ' ' + f.name;
            progressCount.textContent = (i + 1) + ' / ' + toConvert.length;
            progressBar.style.width = '0%';

            var isServerFmt = (FORMATS[fmt] || {}).mode === 'server';
            var chain;
            // The sound of a video first (instant for AAC in mp4/mov); every lane
            // below works on it, and «Авто» on the same codec is the copy itself.
            /* The plan was drawn at the click. A click that beat the header read drew
               it without source facts, and «Авто» → MP3 of a 44.1 kHz video came out
               at the device's 48 kHz — on a loaded machine only (stand 08.10, case I).
               Facts that arrived with the track redraw it: the result does not depend
               on how fast the click was. */
            var source = soundOf(f).then(function (src) {
                f.work = src;
                if (f.src && plans[f.id] && !plans[f.id].known) plans[f.id] = outputPlan(f, fmt, fs);
                return src;
            });
            if (isServerFmt) {
                chain = source.then(function () { return convertFileServer(f, fmt, bitrate, sampleRate); }).then(function (result) {
                    f.jobUrl = result.url;
                    f.jobBlob = null;
                    f.outputName = (result.serverData && result.serverData.files && result.serverData.files[0] && result.serverData.files[0].name) || (baseName(f.name) + '.' + outExtFor(fmt));
                    f.outputSize = (result.serverData && result.serverData.files && result.serverData.files[0] && result.serverData.files[0].size) || 0;
                    f.outputFormat = fmt;
                    f.status = 'success';
                    swapPreviewToResult(f, null, result.url);
                    addToHistory(f, fmt);
                });
            } else {
                chain = source.then(function (src) {
                    if (fs.asItCame && !src.video && (src.lane === 'remux' || src.lane === 'copy') && copyableTrack(f, fmt, fs)) return deliverCopy(f, fmt, src);
                    return convertFileLite(f, fmt, bitrate, sampleRate, channels, plans[f.id] || outputPlan(f, fmt, fs));
                }).then(function (result) {
                    if (result) return result;
                    // FFmpeg path
                    progressFile.textContent = (t.ffmpeg_loading || '') + ' — ' + f.name;
                    return ensureFFmpeg().then(function () {
                        wasmUsed = true;
                        progressFile.textContent = t.processing + ' ' + f.name;
                        // Тот же запасной план, что и у lite-ветки выше: с этой
                        // правки buildArgs берёт из плана РАЗРЯДНОСТЬ, и
                        // отсутствующий план молча вернул бы 16 бит.
                        var args = buildArgs(fmt, bitrate, sampleRate, channels, getSourceChannels(f),
                            plans[f.id] || outputPlan(f, fmt, fs));
                        return convertFileFFmpeg(f, fmt, args).catch(function (err) {
                            var msg = ((err && err.message) || '') + ' ' + ((err && err.name) || '');
                            msg = msg.toLowerCase();
                            // Bridge on decode-class failures AND on wasm runtime
                            // crashes: mp3→opus reproducibly dies with "memory
                            // access out of bounds" inside the combined
                            // decode+encode graph (daily prod class, empty
                            // err_msg rows). Decoding in the browser and handing
                            // FFmpeg clean PCM splits the graph and survives.
                            var wasmCrash = msg.indexOf('memory access out of bounds') !== -1 || msg.indexOf('runtimeerror') !== -1 || msg.indexOf('aborted') !== -1;
                            if (DECODE_FAIL_RX.test(msg) || wasmCrash) {
                                return convertViaBridge(f, fmt, args, wasmCrash).then(function (bridged) {
                                    if (bridged) return bridged;
                                    throw err;
                                });
                            }
                            throw err;
                        });
                    });
                }).then(function (result) {
                    if (!result) throw new Error(t.file_convert_failed || 'Conversion failed');
                    f.jobUrl = result.url;
                    f.jobBlob = result.blob;
                    f.outputName = result.name;
                    f.outputSize = result.blob ? result.blob.size : 0;
                    f.outputFormat = fmt;
                    f.sound = result.sound || null;
                    f.status = 'success';
                    swapPreviewToResult(f, result.blob, null);
                    addToHistory(f, fmt);
                });
            }

            chain.catch(function (err) {
                if (convertCancelled) { f.status = 'ready'; return; }
                try { console.error('Conversion error:', err); } catch (e) {}
                f.status = 'error';
                f.errorReason = classifyAndLabel(err);
                try {
                    window.__aeToolError({
                        error_type: 'convert_file',
                        err_msg: ((err && err.message) || '').substring(0, 800),
                        file_name: f.name,
                        file_size: (f.file && f.file.size) || 0,
                        format: fmt,
                        mode: isServerFmt ? 'server' : 'browser',
                    });
                } catch (e2) {}
            }).then(function () {
                if (myRun) {
                    // 'success' ⇒ the converted file is in the list with a working
                    // download button. Anything else (error, cancel-to-'ready')
                    // delivered nothing; abort leaves the run unrecorded.
                    if (f.status === 'success') myRun.commit(); else myRun.abort();
                }
                i++;
                render();
                nextFile();
            });
        }

        function finish() {
            var successful = toConvert.filter(function (f) { return f.status === 'success'; });
            var failed = toConvert.filter(function (f) { return f.status === 'error'; });
            if (convertCancelled) {
                progressOverlay.classList.remove('show');
            } else if (failed.length > 0) {
                progressTitle.textContent = successful.length + ' / ' + toConvert.length;
                progressFile.textContent = formatMessage(t.error_with_count, { count: failed.length });
            } else {
                progressTitle.textContent = t.done;
                progressFile.textContent = '';
            }
            if (window.__announce) window.__announce(progressTitle.textContent);

            setTimeout(function () {
                progressOverlay.classList.remove('show');
                progressTitle.textContent = t.converting;
                isConverting = false;
                convertBtnText.textContent = t.convert;
                render();
                if (!convertCancelled) {
                    if (successful.length > 0) {
                        toast(formatMessage(successful.length === 1 ? t.done_with_count_one : t.done_with_count, { count: successful.length }), 'success');
                    }
                    if (failed.length > 0) {
                        toast(formatMessage(t.error_with_count, { count: failed.length }), 'error');
                    }
                }
                try {
                    window.__aeToolComplete({
                        format: toConvert.length === 1 ? (toConvert[0].target || globalFormat) : globalFormat,
                        mode: 'browser',
                        wasm: wasmUsed ? 1 : 0,
                        total: toConvert.length,
                        success: successful.length,
                        failed: failed.length,
                        cancelled: convertCancelled ? 1 : 0,
                    });
                } catch (e) {}
                if (!convertCancelled && successful.length === 1 && successful[0].jobBlob) {
                    downloadOne(successful[0].id);
                } else if (!convertCancelled && successful.length === 1 && successful[0].jobUrl) {
                    downloadOne(successful[0].id);   // the same link door as a press
                }
            }, convertCancelled ? 0 : 800);
        }

        nextFile();
    }

    function swapPreviewToResult(f, blob, url) {
        if (f.audioUrl && f.audioUrl.indexOf('blob:') === 0) { try { URL.revokeObjectURL(f.audioUrl); } catch (e) {} }
        f.audioUrl = blob ? URL.createObjectURL(blob) : url;
        f.audioIsResult = true;
        // Deliberately no auto-play() here: a play() this deep after awaits has
        // no user activation and gets NotAllowedError on Safari/Chrome-Android.
        if (currentlyPlaying === f.id) {
            currentlyPlaying = null;
            try { previewAudio.pause(); } catch (e) {}
        }
    }

    // ========== Cancel — actually stops the engine ==========
    convertCancelBtn.addEventListener('click', function () {
        convertCancelled = true;
        // Terminate the in-flight WASM exec; next init() rebuilds cleanly.
        try { if (window.FFmpegProcessor && window.FFmpegProcessor.cancel) window.FFmpegProcessor.cancel(); } catch (e) {}
        aeF('tool_cancel', {});
        progressOverlay.classList.remove('show');
        isConverting = false;
        convertBtn.disabled = false;
        convertBtnText.textContent = t.convert;
        files.forEach(function (f) { if (f.status === 'processing') f.status = 'ready'; });
        render();
    });

    // ========== History ==========
    function getHistory() {
        try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch (e) { return []; }
    }
    function addToHistory(f, fmt) {
        try {
            var h = getHistory();
            h.unshift({ name: baseName(f.name) + '.' + outExtFor(fmt), from: f.ext, to: fmt, date: new Date().toISOString() });
            localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0, 10)));
        } catch (e) {}
        renderHistory();
    }
    function renderHistory() {
        var historyList = $('historyList');
        if (!historyList) return;
        var h = getHistory();
        if (!h.length) {
            historyList.innerHTML = '<div class="history-empty">' + t.no_history + '</div>';
            return;
        }
        historyList.innerHTML = h.map(function (item, idx) {
            var d = '';
            try { d = new Date(item.date).toLocaleDateString(); } catch (e) {}
            return '<div class="history-item" data-idx="' + idx + '">'
                + '<div class="history-icon"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg></div>'
                + '<div class="history-info">'
                + '<div class="history-name" title="' + escapeHtml(item.name) + '">' + escapeHtml(item.name) + '</div>'
                + '<div class="history-meta">' + escapeHtml((item.from || '').toUpperCase()) + ' → ' + escapeHtml((item.to || '').toUpperCase()) + (d ? ' · ' + d : '') + '</div>'
                + '</div>'
                + '<button type="button" class="history-remove" data-idx="' + idx + '" aria-label="Remove entry">'
                + '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
                + '</button>'
                + '</div>';
        }).join('');
        Array.prototype.forEach.call(historyList.querySelectorAll('.history-remove'), function (btn) {
            btn.onclick = function (e) {
                e.stopPropagation();
                try {
                    var h2 = getHistory();
                    h2.splice(+btn.dataset.idx, 1);
                    localStorage.setItem(HISTORY_KEY, JSON.stringify(h2));
                } catch (err) {}
                renderHistory();
            };
        });
    }

    // ========== Downloads ==========
    /*
     * «Download all» and «ZIP» go through window.__batchDeliver.
     *
     * «Download all» used to be a loop of downloads 350 ms apart. A browser hands
     * over ONE file per click and drops the rest in silence (measured on real
     * Chrome: 1 of 3; the delay does not help) — and the ZIP kept its own de-dup
     * that never marked the names it generated as used. openBatch() is called
     * FIRST in the click: on desktop Chromium it opens a folder and writes every
     * file there, one at a time; elsewhere it is one archive. Distinct names on
     * both lanes are the primitive's.
     */
    function successItems() {
        return files.filter(function (f) { return f.status === 'success'; });
    }
    function blobOf(f) {
        if (f.jobBlob) return Promise.resolve(f.jobBlob);
        if (f.jobUrl) return fetch(f.jobUrl).then(function (r) { return r.ok ? r.blob() : null; }).catch(function () { return null; });
        return Promise.resolve(null);
    }
    function deliverAll(archiveOnly) {
        var ok = successItems();
        if (!ok.length) return;
        if (ok.length === 1 && !archiveOnly) { downloadOne(ok[0].id); return; }
        var opts = { slug: 'convert', zipName: 'converted-audio.zip', total: ok.length, archiveOnly: !!archiveOnly };
        var opened = window.__batchDeliver
            ? window.__batchDeliver.openBatch(opts)
            : window.__ensureBatchDeliver().then(function (bd) { return bd.openBatch(opts); });
        opened.then(function (batch) {
            var chain = Promise.resolve();
            ok.forEach(function (f) {
                chain = chain.then(function () { return blobOf(f); }).then(function (b) {
                    if (!b) return;
                    return batch.write(f.outputName || (baseName(f.name) + '.' + outExtFor(f.target || globalFormat)), b);
                });
            });
            return chain.then(function () { return batch.finish(); }, function (e) { return batch.finish().then(function () { throw e; }); });
        }).then(function (res) {
            aeF(archiveOnly ? 'download_zip' : 'download_all', { n: ok.length, via: res && res.via });
        }).catch(function (e) {
            try { console.error('batch delivery error:', e); } catch (e2) {}
            toast(t.zip_error, 'error');
        });
    }
    function downloadAll() { deliverAll(false); }
    function downloadZip() { deliverAll(true); }

    // ========== Wiring (runs immediately — no network gate before listeners) ==========
    uploadZone.addEventListener('tool-uploader:file', function (e) {
        var picked = (e.detail && e.detail.files) ? Array.prototype.slice.call(e.detail.files) : (e.detail && e.detail.file ? [e.detail.file] : []);
        if (picked.length) addFiles(picked);
    });
    $('addMoreBtn').onclick = function () { if (uploadZone.__uploaderOpen) uploadZone.__uploaderOpen(); };
    $('clearAllBtn').onclick = clearAll;
    convertBtn.onclick = convert;
    $('newConvertBtn').onclick = clearAll;
    $('clearHistoryBtn').onclick = function () {
        try { localStorage.removeItem(HISTORY_KEY); } catch (e) {}
        renderHistory();
    };
    downloadAllBtn.onclick = downloadAll;
    downloadZipBtn.onclick = downloadZip;

    // Показ и закрытие — у примитива (popovertarget); клик только наполняет.
    formatSelectorBtn.onclick = function () { prepareFormatSelector(null); };

    // Настройки участвуют в предсказании размера, поэтому строка файла обязана
    // перерисоваться вместе с ними — иначе «→ 24 МБ» останется от прежней частоты.
    var onQualityChange = function () { rememberQuality(); saveSettings(); render(); };
    $('bitrateSelect').onchange = onQualityChange;
    $('sampleRateSelect').onchange = onQualityChange;
    $('channelSelect').onchange = onQualityChange;

    /* Отдать канальный селектор общему модулю.
     *
     * formatEl не передаём: у /convert формат выбирается своим выпадающим
     * списком, а не <select>, поэтому перерисовку зовёт сам тул из
     * updateQualitySelects(). upmix:true — обещание, которое обязан выполнять
     * ЭКСПОРТ: buildArgs подставляет матрицу UpmixModule, а lite-лейн от
     * апмикса отказывается (см. liteBlockedReason). Без этого опцию 5.1 для
     * стерео пришлось бы просто закрыть.
     *
     * onChange: смена каналов меняет предсказанный размер и выбор лейна —
     * строку файла надо перерисовать. Модуль сам зовёт это и после detect(),
     * то есть когда раскладка источника доехала уже ПОСЛЕ выбора. */
    if (channelsLive() && typeof ChannelModule.init === 'function') {
        try {
            ChannelModule.init('channelSelect', {
                tool: 'convert',
                upmix: true,
                onChange: function () { saveSettings(); render(); }
            });
            updateQualitySelects();   // модуль стартует с пустого формата — назовём текущий
            publishSourceChannels();
        } catch (_) {}
    }

    // Container-wide drag&drop (visual feedback + catches drops outside the zone)
    var _dropTimer = null;
    var toolContainer = uploadZone.closest('.converter-container');
    if (toolContainer) {
        toolContainer.addEventListener('dragover', function (e) {
            e.preventDefault();
            toolContainer.classList.add('drop-target');
            clearTimeout(_dropTimer);
            _dropTimer = setTimeout(function () { toolContainer.classList.remove('drop-target'); }, 200);
        });
        toolContainer.addEventListener('dragleave', function (e) {
            if (e.relatedTarget && toolContainer.contains(e.relatedTarget)) return;
            clearTimeout(_dropTimer);
            toolContainer.classList.remove('drop-target');
        });
        toolContainer.addEventListener('drop', function (e) {
            e.preventDefault(); e.stopPropagation();
            clearTimeout(_dropTimer);
            toolContainer.classList.remove('drop-target');
            if (e.target !== uploadZone && !uploadZone.contains(e.target)) {
                if (e.dataTransfer.files && e.dataTransfer.files.length) addFiles(Array.prototype.slice.call(e.dataTransfer.files));
            }
        });
    }
    document.body.addEventListener('dragover', function (e) { e.preventDefault(); });
    document.body.addEventListener('drop', function (e) { e.preventDefault(); });

    // ========== Init ==========
    loadSettings();
    syncFormatButton();
    // Список строится сразу, а не при первом открытии: общий гейт качества
    // (quality-tier.js, pageOffersLossyAudio) узнаёт, что страница предлагает
    // lossy-форматы, по их [data-format] в документе — и по нему же заранее
    // пишет строку тарифа про килобиты. Ленивый список прятал её у самого
    // частого входа (MP3 → общий WAV), пока человек не откроет меню.
    renderFormatList();
    renderHistory();

    // Share Target: file shared from another app via SW cache
    if (new URLSearchParams(location.search).has('shared') && window.caches) {
        caches.open('share-target').then(function (cache) {
            return cache.match('/shared-file');
        }).then(function (resp) {
            if (!resp) return;
            var fileName = decodeURIComponent(resp.headers.get('X-File-Name') || 'shared-audio');
            var contentType = resp.headers.get('Content-Type') || 'audio/mpeg';
            return resp.blob().then(function (blob) {
                var file = new File([blob], fileName, { type: contentType });
                if (window.ToolUploader && typeof window.ToolUploader.inject === 'function') {
                    window.ToolUploader.inject('uploadZone', file, 'share_target');
                } else {
                    addFiles([file]);
                }
                caches.open('share-target').then(function (c) { c.delete('/shared-file'); });
                history.replaceState(null, '', location.pathname);
            });
        }).catch(function () {});
    }
})();
